import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { Gelanggang, PaidUjianCandidate, UserProfile } from '../../types';
import {
  getPaidUjianCandidatesDB,
  saveUjianGradingAssessmentDB,
  deleteUjianGradingAssessmentDB,
  getMembersDB,
  getUjianCohortCompletionStatusDB,
  finalizeUjianGradingBatchRankDB,
  UjianCohortCompletionStatus
} from '../../services/db';
import {
  UJIAN_GRADING_SYLLABUS_SCHEMES,
  getGradingSchemeForTargetBengkung,
  BengkungGradingScheme,
  GradingCriteriaItem
} from '../../data/ujianGradingSyllabus';
import { isBengkungKuningAndAbove } from '../../data/bengkungRanks';
import { GradingHeaderHero } from '../../components/grading/GradingHeaderHero';
import { saveViewScroll, restoreViewScroll, resetScrollToTop } from '../../utils/scrollRestoration';
import {
  Award,
  CheckCircle2,
  AlertCircle,
  Search,
  Filter,
  Printer,
  ChevronRight,
  ChevronLeft,
  Calendar,
  User,
  ShieldCheck,
  Check,
  Star,
  FileText,
  RotateCcw,
  Sparkles,
  ArrowRight,
  X,
  CreditCard,
  Building,
  HelpCircle,
  Clock,
  Lock,
  Settings,
  Plus,
  Trash2,
  Users,
  Cloud,
  Edit3,
  Trophy
} from 'lucide-react';
import {
  formatPublicName,
  isGeneralMemberOnly,
  getHighestRole,
  isUserManagement,
  isSuperAdmin,
  isManagementOrTrainer,
  canViewMemberSensitiveInfo
} from '../../utils/formatters';

export const UjianGradingPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [candidates, setCandidates] = useState<PaidUjianCandidate[]>([]);
  const [membersList, setMembersList] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState<PaidUjianCandidate | null>(null);
  const [cohortStatus, setCohortStatus] = useState<UjianCohortCompletionStatus | null>(null);
  const [isPromotingBatch, setIsPromotingBatch] = useState(false);

  // Superadmin master clearance check
  const isUserSuperAdmin = useMemo(() => isSuperAdmin(currentUser), [currentUser]);

  // Authorization check: strictly Bengkung Kuning and above OR Superadmin can mark grading
  const canUserMarkGrading = useMemo(() => {
    if (isUserSuperAdmin) return true;
    return isBengkungKuningAndAbove(currentUser?.bengkung);
  }, [isUserSuperAdmin, currentUser?.bengkung]);

  // Management clearance check: Superadmin, Jurulatih, Ketua Gelanggang, or AJK/executive
  const isManagement = useMemo(() => {
    return Boolean(currentUser && isManagementOrTrainer(currentUser));
  }, [currentUser]);

  // Check if viewer has authorization to view a candidate's sensitive data (IC, contact, slips)
  const canViewCandidateSensitive = (cand: PaidUjianCandidate | null): boolean => {
    if (!cand || !currentUser) return false;
    // Official management has full clearance
    if (isManagement) return true;

    // Cross-check with members directory for family / self clearance
    const matched = membersList.find(
      (m) =>
        (cand.memberId && m.id.toLowerCase() === cand.memberId.toLowerCase()) ||
        (cand.memberIcNo && m.icNo === cand.memberIcNo) ||
        (cand.memberName && m.name.toLowerCase() === cand.memberName.toLowerCase())
    );

    if (matched) {
      return canViewMemberSensitiveInfo(currentUser, matched);
    }

    // Direct self check
    const isSelf =
      (currentUser.id && cand.memberId && currentUser.id.toLowerCase() === cand.memberId.toLowerCase()) ||
      (currentUser.icNo && cand.memberIcNo && currentUser.icNo.replace(/\D/g, '') === cand.memberIcNo.replace(/\D/g, ''));
    if (isSelf) return true;

    return false;
  };

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGelanggang, setSelectedGelanggang] = useState<string>('all');
  const [selectedLevelFilter, setSelectedLevelFilter] = useState<string>('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');

  // Grading form state for selected candidate
  const [currentScores, setCurrentScores] = useState<Record<string, { score: number; status: 'Lulus' | 'Belum Menguasai' | 'Cemerlang'; notes?: string }>>({});
  const [examinerName, setExaminerName] = useState(currentUser?.name || 'Mohd Hafife Bin Abd Halim');
  const [examDate, setExamDate] = useState(new Date().toISOString().split('T')[0]);
  const [examinerRemarks, setExaminerRemarks] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPrintSlipOpen, setIsPrintSlipOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [activeAssessmentId, setActiveAssessmentId] = useState<string | 'new'>('new');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState<Date | null>(null);
  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(true);
  const isInitialLoadRef = useRef(true);

  // Lock body scroll when modal or dialog is open
  useEffect(() => {
    if (deleteTarget) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [deleteTarget]);

  // Load paid candidates, members roster, and cohort completion status
  const loadData = async () => {
    setIsLoading(true);
    try {
      const [candidatesData, membersData, cohortData] = await Promise.all([
        getPaidUjianCandidatesDB(),
        getMembersDB(),
        getUjianCohortCompletionStatusDB()
      ]);
      setCandidates(candidatesData);
      setMembersList(membersData);
      setCohortStatus(cohortData);

      // Keep selectedCandidate synchronized with fresh DB data
      setSelectedCandidate((prev) => {
        if (!prev) return null;
        const fresh = candidatesData.find(
          (c) =>
            (c.id && c.id === prev.id) ||
            (c.memberId && prev.memberId && c.memberId.toLowerCase() === prev.memberId.toLowerCase()) ||
            (c.registrationId && prev.registrationId && c.registrationId === prev.registrationId)
        );
        return fresh || prev;
      });
    } catch (err) {
      console.error('Error loading data for grading:', err);
      showToast('error', 'Ralat Memuat Data', 'Gagal memuat senarai peserta berbayar atau senarai penguji.');
    } finally {
      setIsLoading(false);
    }
  };

  // Finalize collective promotion for all candidates who passed once all test takers finish
  const handleFinalizeBatchPromotion = async () => {
    setIsPromotingBatch(true);
    try {
      const res = await finalizeUjianGradingBatchRankDB();
      if (res.success) {
        showToast('success', 'Pangkat Bengkung Dikemaskini', res.message);
        await loadData();
      } else {
        showToast('warning', 'Ujian Belum Selesai', res.message);
      }
    } catch (err: any) {
      showToast('error', 'Ralat', err?.message || 'Gagal mengemaskini pangkat bengkung.');
    } finally {
      setIsPromotingBatch(false);
    }
  };

  useEffect(() => {
    loadData();
    window.addEventListener('asmm_db_updated', loadData);
    return () => window.removeEventListener('asmm_db_updated', loadData);
  }, []);

  // Filter members list to extract eligible examiners / assessors (strictly Bengkung Kuning and above)
  const eligibleExaminers = useMemo(() => {
    if (!membersList || membersList.length === 0) return [];
    const seen = new Set<string>();
    return membersList
      .filter((m) => {
        if (!m || !m.name || !m.name.trim()) return false;
        // Exclude ordinary / general members ('Ahli', 'Ahli Biasa', 'Ahli Seumur Hidup', 'Waris')
        if (isGeneralMemberOnly(m)) return false;
        // Exclude inactive or rejected members
        if (m.status === 'Tidak Aktif' || m.status === 'Ditolak') return false;
        // Strictly Bengkung Kuning and above can examine
        if (!isBengkungKuningAndAbove(m.bengkung)) return false;
        // Avoid duplicate entries
        const key = m.name.trim().toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'ms'));
  }, [membersList]);

  // Management roster for Superadmin to mark grading as any management member / examiner
  const managementMembersList = useMemo(() => {
    if (!membersList || membersList.length === 0) return [];
    const seen = new Set<string>();
    return membersList
      .filter((m) => {
        if (!m || !m.name || !m.name.trim()) return false;
        if (m.status === 'Tidak Aktif' || m.status === 'Ditolak') return false;
        // Management committee or Bengkung Kuning & above
        const isMgmt = isUserManagement(m) || isBengkungKuningAndAbove(m.bengkung);
        if (!isMgmt) return false;
        const key = m.name.trim().toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'ms'));
  }, [membersList]);

  // Automatically derive examiner role linked directly from membership profile
  const resolvedExaminerRole = useMemo(() => {
    const targetName = examinerName || currentUser?.name;
    if (!targetName) return 'Penguji Bertauliah ASMM';
    const chosen =
      managementMembersList.find((m) => m.name === targetName) ||
      eligibleExaminers.find((m) => m.name === targetName) ||
      (currentUser && currentUser.name === targetName ? currentUser : null);
    if (chosen) {
      const highest = getHighestRole(chosen);
      if (highest === 'Pengerusi' || highest === 'Superadmin') return 'Dewan Pendekar Perguruan';
      if (highest === 'Ketua Gelanggang') return `Ketua Gelanggang (${chosen.gelanggang || 'ASMM'})`;
      if (highest === 'Timbalan Pengerusi') return 'Timbalan Pengerusi Pertubuhan';
      if (highest.toLowerCase().includes('jurulatih') || highest.toLowerCase().includes('guru')) return `${highest} Bertauliah`;
      return `${highest} Pertubuhan`;
    }
    if (selectedCandidate?.assessment?.assessorRole) {
      return selectedCandidate.assessment.assessorRole;
    }
    return 'Penguji Bertauliah ASMM';
  }, [managementMembersList, eligibleExaminers, examinerName, currentUser, selectedCandidate]);

  // Resolve member display picture, prioritizing real user photo over belt badges
  const getCandidateDisplayPhoto = (cand: PaidUjianCandidate | null): string => {
    if (!cand) return '/default-avatar.svg';

    const matched = membersList.find(
      (m) =>
        (cand.memberId && m.id.toLowerCase() === cand.memberId.toLowerCase()) ||
        (cand.memberIcNo && m.icNo === cand.memberIcNo) ||
        (cand.memberName && m.name.toLowerCase() === cand.memberName.toLowerCase())
    );

    const candidatePhoto = matched?.photoUrl || cand.photoUrl;

    if (candidatePhoto) {
      const isBeltBadge =
        candidatePhoto.includes('/badges/putih') ||
        candidatePhoto.includes('/badges/merah') ||
        candidatePhoto.includes('/badges/kuning') ||
        candidatePhoto.includes('/badges/hitam') ||
        candidatePhoto.includes('/badges/biru');
      if (!isBeltBadge) {
        return candidatePhoto;
      }
    }

    return '/default-avatar.svg';
  };

  // Helper to extract candidate identifier from URL (e.g. /grading/asmm-011 or /grading?candidate=asmm-011)
  const getCandidateIdFromUrl = (): string | null => {
    if (typeof window === 'undefined') return null;
    const searchParams = new URLSearchParams(window.location.search);
    const fromQuery = searchParams.get('candidate') || searchParams.get('id') || searchParams.get('memberId');
    if (fromQuery) return fromQuery.trim().toLowerCase();

    const path = window.location.pathname.replace(/^\//, '').split('?')[0].trim();
    const parts = path.split('/');
    if (parts.length > 1 && (parts[0] === 'grading' || parts[0] === 'ujian-grading')) {
      const slug = parts[1].trim().toLowerCase();
      if (slug && slug !== 'all') return slug;
    }
    return null;
  };

  // Select candidate and update URL
  const handleSelectCandidate = (cand: PaidUjianCandidate) => {
    isInitialLoadRef.current = true;
    setAutoSaveStatus('idle');
    saveViewScroll('grading_candidate_list');
    setSelectedCandidate(cand);
    const slug = (cand.memberId || cand.id).toLowerCase();
    const targetUrl = `/grading/${slug}`;
    if (window.location.pathname !== targetUrl) {
      window.history.pushState({ candidateId: slug }, '', targetUrl);
    }
    resetScrollToTop();
  };

  // Back to candidate list and reset URL to /grading
  const handleBackToList = () => {
    setSelectedCandidate(null);
    if (window.location.pathname !== '/grading') {
      window.history.pushState(null, '', '/grading');
    }
    restoreViewScroll('grading_candidate_list');
  };

  // Auto-sync candidate from URL on data load
  useEffect(() => {
    if (candidates.length === 0) return;
    const targetSlug = getCandidateIdFromUrl();
    if (targetSlug) {
      const matched = candidates.find(
        (c) =>
          (c.memberId && c.memberId.toLowerCase() === targetSlug) ||
          (c.id && c.id.toLowerCase() === targetSlug) ||
          (c.registrationId && c.registrationId.toLowerCase() === targetSlug)
      );
      if (matched && (!selectedCandidate || selectedCandidate.id !== matched.id)) {
        setSelectedCandidate(matched);
      }
    }
  }, [candidates]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const targetSlug = getCandidateIdFromUrl();
      if (targetSlug && candidates.length > 0) {
        const matched = candidates.find(
          (c) =>
            (c.memberId && c.memberId.toLowerCase() === targetSlug) ||
            (c.id && c.id.toLowerCase() === targetSlug) ||
            (c.registrationId && c.registrationId.toLowerCase() === targetSlug)
        );
        if (matched) {
          setSelectedCandidate(matched);
          return;
        }
      }
      setSelectedCandidate(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [candidates]);

  // Always use logged-in user's data as default penguji
  useEffect(() => {
    if (currentUser?.name) {
      setExaminerName(currentUser.name);
    }
  }, [currentUser]);

  // Extract all official assessments for selected candidate
  const candidateAssessments = useMemo(() => {
    if (!selectedCandidate) return [];
    if (selectedCandidate.assessments && selectedCandidate.assessments.length > 0) {
      return selectedCandidate.assessments;
    }
    if (selectedCandidate.assessment) {
      return [selectedCandidate.assessment];
    }
    return [];
  }, [selectedCandidate]);

  // Active assessment record being viewed / edited
  const activeAssessment = useMemo(() => {
    if (activeAssessmentId === 'new') return null;
    return candidateAssessments.find((a) => a.id === activeAssessmentId) || null;
  }, [candidateAssessments, activeAssessmentId]);

  // Check if logged-in user already marked this student
  const myExistingAssessment = useMemo(() => {
    if (!currentUser?.name) return null;
    return candidateAssessments.find(
      (a) => a.assessedBy.toLowerCase().trim() === currentUser.name.toLowerCase().trim()
    ) || null;
  }, [candidateAssessments, currentUser]);

  // Multi-examiner panel consensus calculation
  const panelSummary = useMemo(() => {
    if (candidateAssessments.length === 0) return null;
    const count = candidateAssessments.length;
    const avgScore = Math.round(candidateAssessments.reduce((sum, a) => sum + (a.averageScore || 0), 0) / count);
    const passCount = candidateAssessments.filter((a) => a.overallStatus === 'Lulus').length;
    const failCount = candidateAssessments.filter((a) => a.overallStatus === 'Mengulang').length;
    const isConsensusPass = passCount === count;
    const isConsensusFail = failCount > 0;
    const consensusStatus: 'Lulus' | 'Mengulang' | 'Sedang Dinilai' =
      isConsensusFail ? 'Mengulang' : isConsensusPass ? 'Lulus' : 'Sedang Dinilai';

    return {
      count,
      avgScore,
      passCount,
      failCount,
      isConsensusPass,
      isConsensusFail,
      consensusStatus
    };
  }, [candidateAssessments]);

  // Load an existing assessment into the scoring rubric form
  const loadAssessmentIntoForm = (assessment: any) => {
    setCurrentScores((assessment.scores as any) || {});
    setExaminerName(assessment.assessedBy || currentUser?.name || 'Mohd Hafife Bin Abd Halim');
    setExamDate(assessment.examDate || new Date().toISOString().split('T')[0]);
    setExaminerRemarks(assessment.examinerRemarks || '');
  };

  // Initialize a fresh blank rubric for a new examiner
  const initFreshScores = (targetBengkung: string) => {
    const scheme = getGradingSchemeForTargetBengkung(targetBengkung);
    const initial: Record<string, { score: number; status: 'Lulus' | 'Belum Menguasai' | 'Cemerlang'; notes?: string }> = {};
    scheme.criteria.forEach((item) => {
      initial[item.id] = {
        score: 0,
        status: 'Belum Menguasai',
        notes: ''
      };
    });
    setCurrentScores(initial);

    // Pick an examiner from management roster that has not graded this candidate yet, if available
    const existingAssessorNames = new Set(candidateAssessments.map(a => a.assessedBy.toLowerCase().trim()));
    const unusedManagement = managementMembersList.find(m => !existingAssessorNames.has(m.name.toLowerCase().trim()));

    if (isUserSuperAdmin) {
      if (unusedManagement) {
        setExaminerName(unusedManagement.name);
      } else {
        setExaminerName(currentUser?.name || 'Mohd Hafife Bin Abd Halim');
      }
    } else {
      // For other management, auto use their id detail
      setExaminerName(currentUser?.name || 'Penguji ASMM');
    }

    setExamDate(new Date().toISOString().split('T')[0]);
    setExaminerRemarks('');
  };

  // Switch active examiner tab
  const handleSwitchAssessment = (assessmentId: string | 'new') => {
    if (!selectedCandidate) return;

    if (assessmentId === 'new') {
      // If non-superadmin already has an assessment on this candidate, prevent creating another and redirect to their existing one for editing
      if (!isUserSuperAdmin && myExistingAssessment) {
        showToast('info', 'Penilaian Sedia Ada', 'Anda telah menilai calon ini. Sila buat sebarang kemas kini pada borang sedia ada anda.');
        handleSwitchAssessment(myExistingAssessment.id);
        return;
      }

      if (candidateAssessments.length >= 3) {
        showToast('warning', 'Had Maksimum Penanda Dicapai', 'Maksimum 3 orang penanda markah dibenarkan untuk seorang calon.');
        return;
      }
      isInitialLoadRef.current = true;
      setAutoSaveStatus('idle');
      setActiveAssessmentId('new');
      initFreshScores(selectedCandidate.targetBengkung);
    } else {
      isInitialLoadRef.current = true;
      setAutoSaveStatus('idle');
      setActiveAssessmentId(assessmentId);
      const match = candidateAssessments.find((a) => a.id === assessmentId);
      if (match) {
        loadAssessmentIntoForm(match);
      }
    }
  };

  // Cancel adding a new examiner to the panel
  const handleCancelNewMarker = () => {
    if (candidateAssessments.length > 0) {
      const fallbackTarget = candidateAssessments[0];
      handleSwitchAssessment(fallbackTarget.id);
    } else {
      setActiveAssessmentId('new');
      if (selectedCandidate) {
        initFreshScores(selectedCandidate.targetBengkung);
      }
    }
    showToast('info', 'Penambahan Dibatalkan', `Penambahan ${examinerName || 'penilai'} ke panel penilai telah dibatalkan.`);
  };

  // Determine if active assessment is editable by current user
  const isCurrentAssessmentEditable = useMemo(() => {
    if (!canUserMarkGrading) return false;
    if (isUserSuperAdmin) return true;
    if (activeAssessmentId === 'new') return true;
    if (!activeAssessment) return true;
    return Boolean(
      (currentUser?.name && activeAssessment.assessedBy.toLowerCase().trim() === currentUser.name.toLowerCase().trim()) ||
      (currentUser?.id && (activeAssessment as any).assessorId === currentUser.id) ||
      (currentUser?.id && (activeAssessment as any).assessedById === currentUser.id) ||
      (currentUser?.id && managementMembersList.find(m => m.name.toLowerCase().trim() === activeAssessment.assessedBy.toLowerCase().trim())?.id === currentUser.id)
    );
  }, [canUserMarkGrading, isUserSuperAdmin, activeAssessmentId, activeAssessment, currentUser, managementMembersList]);

  // Delete an assessment from the panel (called by custom confirmation modal)
  const executeDeleteAssessment = async (assessmentId: string, assessorName: string) => {
    try {
      await deleteUjianGradingAssessmentDB(assessmentId);
      showToast('info', 'Penandaan Dipadamkan', `Penandaan markah oleh ${assessorName} telah dipadamkan.`);

      // 1. Immediately update selectedCandidate in state
      let updatedRemaining: any[] = [];
      setSelectedCandidate((prev) => {
        if (!prev) return null;
        const currentList = prev.assessments || (prev.assessment ? [prev.assessment] : []);
        updatedRemaining = currentList.filter((a) => a.id !== assessmentId);

        const remainingPass = updatedRemaining.length > 0 && updatedRemaining.every((a) => a.overallStatus === 'Lulus');
        const remainingFail = updatedRemaining.some((a) => a.overallStatus === 'Mengulang');
        const newStatus: 'Belum Dinilai' | 'Sedang Dinilai' | 'Lulus' | 'Mengulang' =
          updatedRemaining.length === 0
            ? 'Belum Dinilai'
            : remainingFail
              ? 'Mengulang'
              : remainingPass
                ? 'Lulus'
                : 'Sedang Dinilai';

        const latestAssessment = updatedRemaining.length > 0 ? updatedRemaining[updatedRemaining.length - 1] : undefined;

        return {
          ...prev,
          gradingStatus: newStatus,
          assessment: latestAssessment,
          assessments: updatedRemaining
        };
      });

      // 2. Adjust active assessment tab immediately
      isInitialLoadRef.current = true;
      setAutoSaveStatus('idle');
      if (activeAssessmentId === assessmentId) {
        if (updatedRemaining.length > 0) {
          const nextTarget = updatedRemaining[updatedRemaining.length - 1];
          setActiveAssessmentId(nextTarget.id);
          loadAssessmentIntoForm(nextTarget);
        } else {
          setActiveAssessmentId('new');
          if (selectedCandidate) {
            initFreshScores(selectedCandidate.targetBengkung);
          }
        }
      }

      // 3. Reload full dataset from DB to synchronize candidates roster list
      await loadData();
    } catch (err: any) {
      showToast('error', 'Ralat Memadam Penilaian', err?.message || 'Gagal memadam penilaian.');
    }
  };

  // When selectedCandidate changes, initialize grading rubric form
  useEffect(() => {
    isInitialLoadRef.current = true;
    setAutoSaveStatus('idle');
    if (!selectedCandidate) {
      setActiveAssessmentId('new');
      setCurrentScores({});
      setExaminerRemarks('');
      setIsPrintSlipOpen(false);
      return;
    }

    const assessments =
      selectedCandidate.assessments && selectedCandidate.assessments.length > 0
        ? selectedCandidate.assessments
        : selectedCandidate.assessment
          ? [selectedCandidate.assessment]
          : [];

    if (assessments.length > 0) {
      // Prioritize opening the logged in user's assessment if they marked, else the latest
      const myAssessment = currentUser?.name
        ? assessments.find((a) => a.assessedBy.trim().toLowerCase() === currentUser.name.trim().toLowerCase())
        : undefined;
      const target = myAssessment || assessments[assessments.length - 1];
      setActiveAssessmentId(target.id);
      loadAssessmentIntoForm(target);
    } else {
      setActiveAssessmentId('new');
      initFreshScores(selectedCandidate.targetBengkung);
    }
  }, [selectedCandidate?.id, selectedCandidate?.targetBengkung]);

  // Listen for real-time syllabus customizations from settings
  const [syllabusVersion, setSyllabusVersion] = useState(0);
  useEffect(() => {
    const handleSyllabusUpdate = () => setSyllabusVersion((v) => v + 1);
    window.addEventListener('asmm_grading_syllabus_updated', handleSyllabusUpdate);
    return () => window.removeEventListener('asmm_grading_syllabus_updated', handleSyllabusUpdate);
  }, []);

  // Active scheme for selected candidate
  const activeScheme: BengkungGradingScheme = useMemo(() => {
    if (!selectedCandidate) return getGradingSchemeForTargetBengkung('Putih 1');
    return getGradingSchemeForTargetBengkung(selectedCandidate.targetBengkung);
  }, [selectedCandidate, syllabusVersion]);

  // Criteria grouped by category (for separate category tables)
  const groupedCriteria = useMemo(() => {
    const groups: { category: string; items: typeof activeScheme.criteria }[] = [];
    activeScheme.criteria.forEach((item) => {
      const cat = item.category || 'Umum';
      const last = groups[groups.length - 1];
      if (last && last.category === cat) {
        last.items.push(item);
      } else {
        groups.push({ category: cat, items: [item] });
      }
    });
    return groups;
  }, [activeScheme.criteria]);

  // Metrics computation
  const stats = useMemo(() => {
    const totalPaid = candidates.length;
    const passed = candidates.filter(c => c.gradingStatus === 'Lulus').length;
    const failed = candidates.filter(c => c.gradingStatus === 'Mengulang').length;
    const pending = candidates.filter(c => c.gradingStatus === 'Belum Dinilai' || c.gradingStatus === 'Sedang Dinilai').length;
    return { totalPaid, passed, failed, pending };
  }, [candidates]);

  // Filtered candidate roster
  const filteredCandidates = useMemo(() => {
    return candidates.filter(c => {
      // Search (Sensitive fields like IC and Invoice only searchable by authorized management)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchName = c.memberName.toLowerCase().includes(q);
        const matchId = c.memberId.toLowerCase().includes(q);
        const canViewSensitive = canViewCandidateSensitive(c);
        const matchIc = canViewSensitive && (c.memberIcNo || '').toLowerCase().includes(q);
        const matchInv = isManagement && (c.invoiceNo || '').toLowerCase().includes(q);
        if (!matchName && !matchIc && !matchId && !matchInv) return false;
      }

      // Gelanggang
      if (selectedGelanggang !== 'all') {
        if (c.gelanggang !== selectedGelanggang) return false;
      }

      // Level
      if (selectedLevelFilter !== 'all') {
        if (!c.targetBengkung.toLowerCase().includes(selectedLevelFilter.toLowerCase())) return false;
      }

      // Status
      if (selectedStatusFilter !== 'all') {
        if (c.gradingStatus !== selectedStatusFilter) return false;
      }

      return true;
    });
  }, [candidates, searchQuery, selectedGelanggang, selectedLevelFilter, selectedStatusFilter]);

  // Candidate sequence navigation (for Calon Seterusnya & Calon Sebelumnya)
  const activeCandidateList = useMemo(() => {
    return filteredCandidates.length > 0 ? filteredCandidates : candidates;
  }, [filteredCandidates, candidates]);

  const currentCandidateIndex = useMemo(() => {
    if (!selectedCandidate) return -1;
    return activeCandidateList.findIndex(
      (c) => c.id === selectedCandidate.id || c.memberId.toLowerCase() === selectedCandidate.memberId.toLowerCase()
    );
  }, [activeCandidateList, selectedCandidate]);

  const prevCandidate = currentCandidateIndex > 0 ? activeCandidateList[currentCandidateIndex - 1] : null;
  const nextCandidate = currentCandidateIndex >= 0 && currentCandidateIndex < activeCandidateList.length - 1 ? activeCandidateList[currentCandidateIndex + 1] : null;

  // Live scoring calculation for active candidate rubric
  const scoreSummary = useMemo(() => {
    const criteriaCount = activeScheme.criteria.length;
    if (criteriaCount === 0) return { totalScore: 0, maxScore: 0, percentage: 0, grade: 'Belum Dinilai', isPassed: false, ratedCount: 0, criteriaCount: 0 };

    let totalScore = 0;
    let maxPossibleScore = 0;
    let ratedCount = 0;

    activeScheme.criteria.forEach(item => {
      const itemMax = item.maxScore || 5;
      maxPossibleScore += itemMax;
      const scored = currentScores[item.id]?.score || 0;
      if (scored > 0) {
        ratedCount++;
        totalScore += scored;
      }
    });

    const percentage = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;
    const passThreshold = activeScheme.passingScorePercentage || 60;
    const isCompleted = ratedCount === criteriaCount && criteriaCount > 0;
    const isPassed = isCompleted && percentage >= passThreshold;

    let grade = 'Belum Lengkap';
    if (isCompleted) {
      if (percentage >= 85) grade = 'A (Cemerlang)';
      else if (percentage >= 70) grade = 'B (Kepujian)';
      else if (percentage >= passThreshold) grade = 'C (Lulus)';
      else grade = 'D (Mengulang)';
    }

    return { totalScore, maxScore: maxPossibleScore, percentage, grade, isPassed, isCompleted, ratedCount, criteriaCount };
  }, [activeScheme, currentScores]);

  // Derivation of up to 3 official juris for the printable slip
  const slipJuris = useMemo(() => {
    if (candidateAssessments.length > 0) {
      return [
        candidateAssessments[0] || null,
        candidateAssessments[1] || null,
        candidateAssessments[2] || null
      ];
    }
    return [
      {
        id: 'live',
        assessedBy: examinerName,
        assessorRole: resolvedExaminerRole,
        scores: currentScores,
        averageScore: scoreSummary.percentage,
        overallStatus: scoreSummary.isPassed ? 'Lulus' : 'Mengulang',
        examinerRemarks: examinerRemarks
      },
      null,
      null
    ];
  }, [candidateAssessments, examinerName, resolvedExaminerRole, currentScores, scoreSummary, examinerRemarks]);

  const [slipJuri1, slipJuri2, slipJuri3] = slipJuris;

  // Check whether candidate has completed assessment / official content from Penguji
  const hasExaminerContent = useMemo(() => {
    if (!selectedCandidate) return false;
    const a = selectedCandidate.assessment;
    if (!a) return false;
    const hasAssessor = Boolean(a.assessedBy && a.assessedBy.trim());
    const hasRemarks = Boolean(a.examinerRemarks && a.examinerRemarks.trim());
    const hasStatus = Boolean(a.overallStatus && a.overallStatus.trim());
    const isGraded = selectedCandidate.gradingStatus === 'Lulus' || selectedCandidate.gradingStatus === 'Mengulang';
    return (hasAssessor || hasRemarks || hasStatus) && isGraded;
  }, [selectedCandidate]);

  // Seamless Debounced Auto-Save for Grading Marks & Remarks
  useEffect(() => {
    if (!selectedCandidate || !canUserMarkGrading || !isAutoSaveEnabled) return;

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    // Only auto-save if examiner has actually rated at least one criterion or typed remarks
    const hasAnyContent = scoreSummary.ratedCount > 0 || examinerRemarks.trim().length > 0;
    if (!hasAnyContent) return;

    setAutoSaveStatus('saving');
    const timer = setTimeout(async () => {
      try {
        const isPassed = scoreSummary.isPassed;
        const overallStatus = !scoreSummary.isCompleted
          ? 'Sedang Dinilai'
          : isPassed
            ? 'Lulus'
            : 'Mengulang';

        const existingForExaminer = candidateAssessments.find(
          (a) => a.assessedBy.toLowerCase().trim() === examinerName.toLowerCase().trim()
        );

        // Enforce max 3 penanda: if adding a new examiner and candidate already has 3 assessors, do not auto-save
        if (!existingForExaminer && activeAssessmentId === 'new' && candidateAssessments.length >= 3) {
          return;
        }

        // If non-superadmin is viewing another person's assessment, do not auto-save
        if (!isCurrentAssessmentEditable) {
          return;
        }

        const assessmentId =
          activeAssessmentId !== 'new'
            ? activeAssessmentId
            : existingForExaminer
              ? existingForExaminer.id
              : `grd-${Date.now().toString().slice(-8)}`;

        const certNo =
          activeAssessment?.certificateNo ||
          existingForExaminer?.certificateNo ||
          (isPassed && scoreSummary.isCompleted
            ? `ASMM/GRD/${new Date().getFullYear()}/${selectedCandidate.memberId.replace(/[^0-9]/g, '').padStart(3, '0')}`
            : undefined);

        const payload = {
          id: assessmentId,
          candidateRegistrationId: selectedCandidate.registrationId,
          invoiceId: selectedCandidate.invoiceId,
          memberId: selectedCandidate.memberId,
          memberName: selectedCandidate.memberName,
          memberIcNo: selectedCandidate.memberIcNo,
          memberPhone: selectedCandidate.memberPhone,
          gelanggang: selectedCandidate.gelanggang,
          currentBengkung: selectedCandidate.currentBengkung,
          targetBengkung: selectedCandidate.targetBengkung,
          paidAmount: selectedCandidate.amountPaid,
          paymentRef: selectedCandidate.invoiceNo,
          paidAt: selectedCandidate.paidAt,
          examDate: examDate,
          assessedBy: examinerName.trim() || 'Penguji ASMM',
          assessorRole: resolvedExaminerRole,
          overallStatus: overallStatus as any,
          overallGrade: scoreSummary.grade,
          averageScore: scoreSummary.percentage,
          certificateNo: certNo,
          examinerRemarks: examinerRemarks.trim(),
          scores: Object.entries(currentScores).reduce((acc, [k, v]) => {
            const crit = activeScheme.criteria.find(c => c.id === k);
            acc[k] = {
              itemId: k,
              itemTitle: crit?.title || k,
              category: crit?.category,
              score: v.score,
              status: v.status,
              notes: v.notes
            };
            return acc;
          }, {} as Record<string, any>),
          updatedAt: new Date().toISOString()
        };

        await saveUjianGradingAssessmentDB(payload);
        if (activeAssessmentId === 'new') {
          setActiveAssessmentId(payload.id);
        }

        setSelectedCandidate((prev) => {
          if (!prev) return null;
          const prevList = prev.assessments || (prev.assessment ? [prev.assessment] : []);
          const idx = prevList.findIndex((a) => a.id === payload.id);
          const updatedList = idx >= 0 ? prevList.map((a, i) => (i === idx ? payload : a)) : [...prevList, payload];

          return {
            ...prev,
            gradingStatus: overallStatus,
            assessment: payload,
            assessments: updatedList
          };
        });

        setAutoSaveStatus('saved');
        setLastAutoSavedAt(new Date());
      } catch (e) {
        console.warn('Auto-save error:', e);
        setAutoSaveStatus('error');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [currentScores, examinerRemarks, examDate, examinerName, isAutoSaveEnabled, isCurrentAssessmentEditable, candidateAssessments.length]);

  // Handlers for scoring (Restricted to Bengkung Kuning and above)
  const handleScoreChange = (itemId: string, newScore: number) => {
    if (!canUserMarkGrading) {
      showToast('warning', 'Akses Terhad', 'Hanya pemegang Bengkung Kuning dan ke atas dibenarkan mengisi markah ujian.');
      return;
    }
    if (!isCurrentAssessmentEditable) {
      showToast('info', 'Paparan Sahaja', `Borang ini ditanda oleh ${activeAssessment?.assessedBy || 'penilai lain'}. Anda sedang melihat penilaian penilai lain.`);
      return;
    }
    setCurrentScores(prev => {
      const current = prev[itemId] || { score: 0, status: 'Belum Menguasai', notes: '' };
      let newStatus: 'Lulus' | 'Belum Menguasai' | 'Cemerlang' = 'Belum Menguasai';
      if (newScore >= 5) newStatus = 'Cemerlang';
      else if (newScore >= 3) newStatus = 'Lulus';

      return {
        ...prev,
        [itemId]: {
          ...current,
          score: newScore,
          status: newStatus
        }
      };
    });
  };

  const handleNotesChange = (itemId: string, note: string) => {
    if (!canUserMarkGrading || !isCurrentAssessmentEditable) return;
    setCurrentScores(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || { score: 0, status: 'Belum Menguasai' }),
        notes: note
      }
    }));
  };

  // Quick Action: Mark all items as passed with 4 or 5
  const handleMarkAllPassed = (scoreVal: number = 5) => {
    if (!canUserMarkGrading) {
      showToast('warning', 'Akses Terhad', 'Hanya pemegang Bengkung Kuning dan ke atas dibenarkan mengisi markah ujian.');
      return;
    }
    if (!isCurrentAssessmentEditable) {
      showToast('info', 'Paparan Sahaja', `Borang ini ditanda oleh ${activeAssessment?.assessedBy || 'penilai lain'}. Anda tidak dibenarkan mengubah markah penilai lain.`);
      return;
    }
    const updated: Record<string, { score: number; status: 'Lulus' | 'Belum Menguasai' | 'Cemerlang'; notes?: string }> = {};
    activeScheme.criteria.forEach(item => {
      updated[item.id] = {
        score: scoreVal,
        status: scoreVal >= 5 ? 'Cemerlang' : 'Lulus',
        notes: currentScores[item.id]?.notes || 'Menguasai gerak dengan baik.'
      };
    });
    setCurrentScores(updated);
    showToast('info', 'Semua Kriteria Dinilai', `Semua ${activeScheme.criteria.length} kriteria telah ditandai ${scoreVal}/5.`);
  };

  // Reset scoring rubric
  const handleResetScores = () => {
    if (!canUserMarkGrading || !isCurrentAssessmentEditable) return;
    const reset: Record<string, { score: number; status: 'Lulus' | 'Belum Menguasai' | 'Cemerlang'; notes?: string }> = {};
    activeScheme.criteria.forEach(item => {
      reset[item.id] = {
        score: 0,
        status: 'Belum Menguasai',
        notes: ''
      };
    });
    setCurrentScores(reset);
  };

  // Save assessment
  const handleSaveAssessment = async () => {
    if (!selectedCandidate) return;

    if (!canUserMarkGrading) {
      showToast('error', 'Akses Ditolak', 'Hanya pemegang Bengkung Kuning dan ke atas dibenarkan mengesahkan penilaian rasmi.');
      return;
    }

    if (!isCurrentAssessmentEditable) {
      showToast('error', 'Akses Terhad', `Borang ini ditanda oleh ${activeAssessment?.assessedBy || 'penilai lain'}. Anda tidak dibenarkan mengubah penilaian penilai lain.`);
      return;
    }

    if (scoreSummary.ratedCount < scoreSummary.criteriaCount) {
      showToast('warning', 'Penilaian Belum Lengkap', `Sila nilai kesemua ${scoreSummary.criteriaCount} kriteria silibus (${scoreSummary.ratedCount}/${scoreSummary.criteriaCount} selesai).`);
      return;
    }

    // Determine existing assessment ID for this examiner, or generate a new one
    const existingForExaminer = candidateAssessments.find(
      (a) => a.assessedBy.toLowerCase().trim() === examinerName.toLowerCase().trim()
    );

    // Enforce max 3 penanda limit
    if (!existingForExaminer && activeAssessmentId === 'new' && candidateAssessments.length >= 3) {
      showToast('warning', 'Had Maksimum Penanda Dicapai', 'Maksimum 3 orang penanda markah dibenarkan untuk seorang calon.');
      return;
    }

    setIsSaving(true);
    try {
      const isPassed = scoreSummary.isPassed;

      // Determine existing assessment ID for this examiner, or generate a new one
      const existingForExaminer = candidateAssessments.find(
        (a) => a.assessedBy.toLowerCase().trim() === examinerName.toLowerCase().trim()
      );

      const assessmentId =
        activeAssessmentId !== 'new'
          ? activeAssessmentId
          : existingForExaminer
            ? existingForExaminer.id
            : `grd-${Date.now().toString().slice(-8)}`;

      const certNo =
        activeAssessment?.certificateNo ||
        existingForExaminer?.certificateNo ||
        (isPassed
          ? `ASMM/GRD/${new Date().getFullYear()}/${selectedCandidate.memberId.replace(/[^0-9]/g, '').padStart(3, '0')}`
          : undefined);

      const payload = {
        id: assessmentId,
        candidateRegistrationId: selectedCandidate.registrationId,
        invoiceId: selectedCandidate.invoiceId,
        memberId: selectedCandidate.memberId,
        memberName: selectedCandidate.memberName,
        memberIcNo: selectedCandidate.memberIcNo,
        memberPhone: selectedCandidate.memberPhone,
        gelanggang: selectedCandidate.gelanggang,
        currentBengkung: selectedCandidate.currentBengkung,
        targetBengkung: selectedCandidate.targetBengkung,
        paidAmount: selectedCandidate.amountPaid,
        paymentRef: selectedCandidate.invoiceNo,
        paidAt: selectedCandidate.paidAt,
        examDate: examDate,
        assessedBy: examinerName.trim() || 'Penguji ASMM',
        assessorRole: resolvedExaminerRole,
        overallStatus: (isPassed ? 'Lulus' : 'Mengulang') as 'Lulus' | 'Mengulang',
        overallGrade: scoreSummary.grade,
        averageScore: scoreSummary.percentage,
        certificateNo: certNo,
        examinerRemarks: examinerRemarks.trim(),
        scores: Object.entries(currentScores).reduce((acc, [k, v]) => {
          const crit = activeScheme.criteria.find(c => c.id === k);
          acc[k] = {
            itemId: k,
            itemTitle: crit?.title || k,
            category: crit?.category,
            score: v.score,
            status: v.status,
            notes: v.notes
          };
          return acc;
        }, {} as Record<string, any>),
        updatedAt: new Date().toISOString()
      };

      await saveUjianGradingAssessmentDB(payload);
      setActiveAssessmentId(payload.id);

      // Refresh candidate state from DB
      await loadData();

      // Update selectedCandidate locally
      setSelectedCandidate((prev: PaidUjianCandidate | null) => {
        if (!prev) return null;
        const prevList = prev.assessments || (prev.assessment ? [prev.assessment] : []);
        const idx = prevList.findIndex((a) => a.id === payload.id);
        const updatedList = idx >= 0 ? prevList.map((a, i) => (i === idx ? payload : a)) : [...prevList, payload];

        return {
          ...prev,
          gradingStatus: payload.overallStatus,
          assessment: payload,
          assessments: updatedList
        };
      });

      const isUpdating = activeAssessmentId !== 'new' || Boolean(existingForExaminer);
      showToast(
        'success',
        isPassed
          ? (isUpdating ? 'Penilaian Dikemas Kini: LULUS!' : 'Penilaian Disimpan: LULUS!')
          : (isUpdating ? 'Penilaian Dikemas Kini: Mengulang' : 'Penilaian Disimpan: Mengulang'),
        `Borang penilaian oleh ${examinerName} untuk ${formatPublicName(selectedCandidate.memberName)} telah ${isUpdating ? 'dikemas kini' : 'disimpan'}.`
      );
    } catch (err: any) {
      console.error('Error saving grading assessment:', err);
      showToast('error', 'Ralat Menyimpan Penilaian', err?.message || 'Gagal menyimpan keputusan ujian.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white min-h-screen text-slate-900 pb-16">
      {/* 1. STANDARDIZED HERO FEATURE CARD - ONLY VISIBLE ON ROSTER VIEW */}
      {!selectedCandidate && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <GradingHeaderHero
            activeTab="grading"
            badgeTag="Silibus Rasmi Pindaan 2023 • Dewan Pendekar ASMM"
            badgeIcon={Award}
            badgeColorClass="bg-red-50 text-red-700 border-red-200/80"
            title="Pengurusan & Penilaian Ujian Bengkung"
            subtitle="Portal pentaksiran rasmi untuk penilai & ketua gelanggang. Dinilai terus mengikut sukatan mata pelajaran rasmi PDF persatuan dengan integrasi calon yang telah melunaskan yuran ujian."
            metrics={[
              { value: stats.totalPaid, label: 'Calon Berbayar', colorClass: 'text-slate-900' },
              { value: stats.pending, label: 'Belum Dinilai', colorClass: 'text-blue-600' },
              { value: stats.passed, label: 'Lulus', colorClass: 'text-amber-600' },
              { value: stats.failed, label: 'Mengulang', colorClass: 'text-red-600' },
            ]}
          />
        </div>
      )}

      {/* 2. MAIN WORKSPACE (ZERO-MODAL WORKFLOW) */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {!selectedCandidate ? (
          /* ======================================================== */
          /* ROSTER VIEW: LIST OF CANDIDATES WHO HAVE PAID FOR UJIAN  */
          /* ======================================================== */
          <div className="space-y-4 sm:space-y-6">
            {/* Cohort Exam Completion & Bengkung Integrity Status Card */}
            {cohortStatus && cohortStatus.totalCandidates > 0 && (
              <div className={`bg-white rounded-3xl border ${
                cohortStatus.isAllTestsCompleted
                  ? 'border-amber-300 shadow-sm'
                  : 'border-slate-200/80 shadow-xs'
              } p-4 sm:p-5 transition-all`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
                      cohortStatus.isAllTestsCompleted
                        ? 'bg-amber-100 text-amber-800 border border-amber-200'
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      {cohortStatus.isAllTestsCompleted ? (
                        <Sparkles className="w-5 h-5 text-amber-600" />
                      ) : (
                        <ShieldCheck className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-xs sm:text-sm font-black text-slate-900 tracking-tight">
                          {cohortStatus.isAllTestsCompleted
                            ? 'Kesemua Calon Selesai Menduduki Ujian'
                            : 'Integriti Kenaikan Bengkung Sesi Ujian'}
                        </h3>
                        <span className={`text-[10px] font-black px-2.5 py-0.5 rounded-full ${
                          cohortStatus.isAllTestsCompleted
                            ? 'bg-amber-100 text-amber-900 border border-amber-300'
                            : 'bg-blue-50 text-blue-800 border border-blue-200'
                        }`}>
                          {cohortStatus.isAllTestsCompleted ? 'Selesai 100%' : 'Sedang Berlangsung'}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                        {cohortStatus.completedGradingCount} daripada {cohortStatus.totalCandidates} calon selesai dinilai
                        {cohortStatus.pendingGradingCount > 0
                          ? ` (Baki ${cohortStatus.pendingGradingCount} calon belum selesai)`
                          : ` • ${cohortStatus.passedCandidates.length} Lulus, ${cohortStatus.failedCandidates.length} Mengulang`}
                      </p>
                    </div>
                  </div>

                  {/* Actions / Status Pill */}
                  <div className="flex items-center gap-2 self-start sm:self-center">
                    {cohortStatus.isAllTestsCompleted ? (
                      canUserMarkGrading && (
                        <button
                          type="button"
                          onClick={handleFinalizeBatchPromotion}
                          disabled={isPromotingBatch}
                          className="px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-black text-xs shadow-xs transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                          title="Sahkan kenaikan taraf bengkung calon yang lulus ke profil murid"
                        >
                          <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                          <span>{isPromotingBatch ? 'Mengemaskini...' : 'Sahkan Kenaikan Bengkung'}</span>
                        </button>
                      )
                    ) : (
                      <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200/80 px-3 py-1 rounded-full flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-amber-600" />
                        <span>Pangkat bengkung kekal semasa ujian</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Informative Rule Callout */}
                {!cohortStatus.isAllTestsCompleted ? (
                  <div className="mt-3.5 pt-3 border-t border-slate-100 flex items-start gap-2.5 text-[11px] text-slate-600">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="leading-relaxed">
                      <strong>Peraturan PSSASMM:</strong> Walaupun calon telah memperoleh keputusan <em>LULUS</em>, pangkat bengkung profil murid belum dinaikkan lagi. Pangkat bengkung hanya akan dinaikkan serentak untuk semua murid yang lulus selepas <strong>kesemua {cohortStatus.totalCandidates} calon selesai menduduki kesemua ujian</strong>.
                    </p>
                  </div>
                ) : (
                  <div className="mt-3.5 pt-3 border-t border-amber-100 flex items-start gap-2.5 text-[11px] text-amber-900">
                    <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="leading-relaxed">
                      Kesemua calon telah selesai menduduki ujian. Pangkat bengkung bagi <strong>{cohortStatus.passedCandidates.length} orang murid yang lulus</strong> kini telah dinaikkan taraf secara rasmi ke profil murid masing-masing.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Filter and Search Bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-3.5 sm:p-5 shadow-xs space-y-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                {/* Search Box */}
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder={isManagement ? "Cari nama, no. IC, invois..." : "Cari nama atau ID pesilat..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 text-xs font-semibold rounded-xl border border-slate-200 focus:outline-none focus:border-red-600 bg-slate-50/50"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Filter Dropdowns Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {/* Gelanggang */}
                  <select
                    value={selectedGelanggang}
                    onChange={(e) => setSelectedGelanggang(e.target.value)}
                    className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-red-600"
                  >
                    <option value="all">Semua Gelanggang</option>
                    <option value="Pusaka Warisan">Pusaka Warisan</option>
                    <option value="Wadi AnNour">Wadi AnNour</option>
                    <option value="Paya Besar">Paya Besar</option>
                    <option value="ASMM">Pusat ASMM</option>
                  </select>

                  {/* Level */}
                  <select
                    value={selectedLevelFilter}
                    onChange={(e) => setSelectedLevelFilter(e.target.value)}
                    className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-red-600"
                  >
                    <option value="all">Semua Peringkat</option>
                    <option value="Putih 1">Putih 1 (Cula 1)</option>
                    <option value="Putih 2">Putih 2 (Cula 2)</option>
                    <option value="Putih 3">Putih 3 (Cula 3)</option>
                    <option value="Merah">Merah (Kosong)</option>
                    <option value="Merah 1">Merah 1 (Cula 1)</option>
                    <option value="Merah 2">Merah 2 (Cula 2)</option>
                    <option value="Merah 3">Merah 3 (Cula 3)</option>
                    <option value="Kuning">Kuning</option>
                  </select>

                  {/* Status */}
                  <select
                    value={selectedStatusFilter}
                    onChange={(e) => setSelectedStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-200 bg-white text-slate-700 focus:outline-none focus:border-red-600"
                  >
                    <option value="all">Semua Status</option>
                    <option value="Belum Dinilai">Belum Dinilai</option>
                    <option value="Lulus">Lulus</option>
                    <option value="Mengulang">Mengulang</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Results Counter Banner */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs font-bold text-slate-500 px-1 gap-1">
              <span>Menunjukkan {filteredCandidates.length} peserta berbayar</span>
              <span className="text-slate-400 text-[11px] sm:text-xs">Klik mana-mana calon untuk membuka rubrik penilaian</span>
            </div>

            {/* Candidates Card Stack */}
            {isLoading ? (
              <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center text-slate-500 text-xs">
                <div className="animate-spin w-8 h-8 border-3 border-red-600 border-t-transparent rounded-full mx-auto mb-3" />
                <span>Memuat senarai peserta berbayar...</span>
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center space-y-3">
                <HelpCircle className="w-12 h-12 text-slate-300 mx-auto" />
                <h3 className="font-bold text-slate-800 text-base">Tiada Peserta Dijumpai</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Tiada rekod pesilat berbayar yang sepadan dengan carian atau penapis anda. Cuba tukar kriteria carian.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 sm:gap-4">
                {filteredCandidates.map((cand) => {
                  const scheme = getGradingSchemeForTargetBengkung(cand.targetBengkung);
                  const isPassed = cand.gradingStatus === 'Lulus';
                  const isFailed = cand.gradingStatus === 'Mengulang';

                  return (
                    <div
                      key={cand.id}
                      onClick={() => handleSelectCandidate(cand)}
                      className="group bg-white rounded-2xl p-4 sm:p-5 border border-slate-200 hover:border-red-300 hover:shadow-md active:scale-[0.99] transition-all cursor-pointer flex flex-col justify-between gap-3.5"
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        {/* Left Member Display Picture Container (Fixed standard size) */}
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-slate-100 border border-slate-200 overflow-hidden relative shrink-0 shadow-2xs">
                          <img
                            src={getCandidateDisplayPhoto(cand)}
                            alt={cand.memberName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/default-avatar.svg';
                            }}
                          />
                          {/* Overlapping Status Dot (only if graded: passed or failed; no blue dot if not yet) */}
                          {isPassed && (
                            <span
                              className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold absolute top-1 right-1 shadow-xs text-white ring-2 ring-white bg-emerald-600"
                            >
                              ✓
                            </span>
                          )}
                          {isFailed && (
                            <span
                              className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold absolute top-1 right-1 shadow-xs text-white ring-2 ring-white bg-red-600"
                            >
                              ✗
                            </span>
                          )}
                        </div>

                        {/* Candidate Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <span className="text-[10px] font-black tracking-wide px-2 py-0.5 rounded-md bg-slate-900 text-white">
                              {cand.memberId}
                            </span>
                            <span className="text-[10px] font-bold text-slate-500">
                              {cand.gelanggang}
                            </span>
                          </div>

                          <h3 className="text-sm sm:text-base font-extrabold text-slate-900 mt-1 truncate group-hover:text-red-600 transition leading-snug">
                            {formatPublicName(cand.memberName)}
                          </h3>

                          {canViewCandidateSensitive(cand) && cand.memberIcNo && (
                            <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                              No. IC: {cand.memberIcNo}
                            </div>
                          )}

                          {/* Bengkung Transition Pill */}
                          <div className="mt-2 flex items-center gap-1.5 text-xs font-bold flex-wrap">
                            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">
                              {cand.currentBengkung}
                            </span>
                            <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 text-[10px] font-black">
                              Sasaran: {cand.targetBengkung}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Action Bar */}
                      <div className="pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[11px] font-semibold ${isPassed ? 'text-emerald-600 font-bold flex items-center gap-1' : isFailed ? 'text-red-600 font-bold' : 'text-slate-400'}`}>
                            {isPassed ? (
                              <>
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                <span>Keputusan: Lulus</span>
                                {cohortStatus && !cohortStatus.isAllTestsCompleted && (
                                  <span className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200/80 px-1.5 py-0.5 rounded ml-1">
                                    Bengkung kekal semasa ujian
                                  </span>
                                )}
                              </>
                            ) : isFailed ? (
                              'Keputusan: Mengulang'
                            ) : (
                              'Status: Menunggu Ujian'
                            )}
                          </span>

                          {cand.assessments && cand.assessments.length > 0 && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 inline-flex items-center gap-1">
                              <Users className="w-2.5 h-2.5 text-slate-500" />
                              <span>{cand.assessments.length} Penilai</span>
                            </span>
                          )}

                          {cand.ujianBertulis ? (
                            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${cand.ujianBertulis.isPassed ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                              <FileText className="w-2.5 h-2.5" />
                              <span>Teori: {cand.ujianBertulis.bestPercentage}%</span>
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold text-slate-400 inline-flex items-center gap-1">
                              <FileText className="w-2.5 h-2.5" />
                              <span>Teori: Belum</span>
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectCandidate(cand);
                          }}
                          className={`inline-flex items-center gap-1 font-bold group-hover:translate-x-0.5 transition-transform cursor-pointer px-3 py-1.5 rounded-xl border text-xs ${canUserMarkGrading
                            ? isPassed
                              ? 'text-slate-700 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border-slate-200'
                              : 'text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border-red-200'
                            : 'text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 border-slate-200'
                            }`}
                        >
                          <span>
                            {canUserMarkGrading
                              ? cand.gradingStatus === 'Belum Dinilai'
                                ? 'Mula Pemarkahan'
                                : isPassed
                                  ? 'Pemarkahan'
                                  : 'Nilai Semula'
                              : 'Lihat Pemarkahan'}
                          </span>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* ======================================================== */
          /* EVALUATION VIEW: ACTIVE CANDIDATE GRADING RUBRIC FORM    */
          /* ======================================================== */
          <div className="space-y-4 sm:space-y-6 animate-fade-in">
            {/* 1. UNIFIED TOP NAVIGATION & CALON PAGER BAR (No Cetak Slip here - moved to bottom most after catatan) */}
            <div className="bg-white border border-slate-200/90 rounded-2xl p-2.5 sm:p-3 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              {/* Left: Kembali ke Senarai Calon */}
              <div className="flex items-center justify-between sm:justify-start gap-2">
                <button
                  type="button"
                  onClick={handleBackToList}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition cursor-pointer shadow-2xs group"
                >
                  <ChevronLeft className="w-4 h-4 text-slate-500 group-hover:-translate-x-0.5 transition-transform" />
                  <span>Kembali ke Senarai Calon</span>
                </button>

                {/* Mobile-only compact counter badge */}
                <span className="sm:hidden px-2.5 py-1 rounded-full bg-slate-100 text-[11px] font-black text-slate-800 border border-slate-200">
                  Calon {currentCandidateIndex >= 0 ? currentCandidateIndex + 1 : 1}/{activeCandidateList.length}
                </span>
              </div>

              {/* Right: Dedicated Next/Prev Pager (Always visible, disabled at boundary) */}
              <div className="flex items-center justify-between sm:justify-end gap-2">
                {/* Prev Button: Always visible, disabled if currentCandidateIndex <= 0 */}
                <button
                  type="button"
                  onClick={() => prevCandidate && handleSelectCandidate(prevCandidate)}
                  disabled={!prevCandidate}
                  className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 sm:px-3.5 py-1.5 rounded-xl text-xs font-bold transition shadow-2xs select-none ${prevCandidate
                    ? 'bg-white hover:bg-slate-100 text-slate-800 border border-slate-200 cursor-pointer active:scale-98'
                    : 'bg-slate-100 text-slate-400 border border-slate-200/60 cursor-not-allowed opacity-50'
                    }`}
                  title={prevCandidate ? `Calon Sebelumnya: ${formatPublicName(prevCandidate.memberName)}` : 'Tiada calon sebelumnya (Awal senarai)'}
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Sebelumnya</span>
                </button>

                {/* Desktop Counter Badge */}
                <span className="hidden sm:inline-block px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs font-black text-slate-900 shadow-2xs whitespace-nowrap">
                  Calon {currentCandidateIndex >= 0 ? currentCandidateIndex + 1 : 1} / {activeCandidateList.length}
                </span>

                {/* Next Button: Always visible, disabled if currentCandidateIndex >= activeCandidateList.length - 1 */}
                <button
                  type="button"
                  onClick={() => nextCandidate && handleSelectCandidate(nextCandidate)}
                  disabled={!nextCandidate}
                  className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 sm:px-4 py-1.5 rounded-xl text-xs font-black transition shadow-2xs select-none ${nextCandidate
                    ? 'bg-red-600 hover:bg-red-700 text-white shadow-xs cursor-pointer active:scale-98'
                    : 'bg-slate-100 text-slate-400 border border-slate-200/60 cursor-not-allowed opacity-50'
                    }`}
                  title={nextCandidate ? `Calon Seterusnya: ${formatPublicName(nextCandidate.memberName)}` : 'Tiada calon seterusnya (Akhir senarai)'}
                >
                  <span>Calon Seterusnya</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* 3. CANDIDATE PROFILE HERO BANNER */}
            <div className="bg-white rounded-3xl border border-slate-200/90 p-4 sm:p-6 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 sm:gap-6">
              {/* Left: Avatar & Candidate Identity */}
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-slate-50 border-2 border-slate-200 overflow-hidden relative shrink-0 shadow-sm flex items-center justify-center">
                  <img
                    src={getCandidateDisplayPhoto(selectedCandidate)}
                    alt={selectedCandidate.memberName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/default-avatar.svg';
                    }}
                  />
                  {selectedCandidate.gradingStatus === 'Lulus' && (
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-black absolute top-1 right-1 shadow-md ring-2 ring-white" title="Lulus Penilaian">
                      ✓
                    </span>
                  )}
                  {selectedCandidate.gradingStatus === 'Mengulang' && (
                    <span className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-black absolute top-1 right-1 shadow-md ring-2 ring-white" title="Mengulang Penilaian">
                      ✗
                    </span>
                  )}
                  {selectedCandidate.gradingStatus === 'Sedang Dinilai' && (
                    <span className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-black absolute top-1 right-1 shadow-md ring-2 ring-white" title="Sedang Dinilai">
                      ✎
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="px-2.5 py-0.5 rounded-md bg-slate-900 text-white text-[10px] font-black tracking-wider shadow-2xs">
                      {selectedCandidate.memberId}
                    </span>
                    <span className="px-2.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold">
                      {selectedCandidate.gelanggang}
                    </span>
                    {selectedCandidate.currentBengkung && (
                      <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-semibold">
                        Semasa: {selectedCandidate.currentBengkung}
                      </span>
                    )}
                  </div>

                  <h2 className="text-lg sm:text-2xl font-black text-slate-900 mt-1 truncate tracking-tight">
                    {formatPublicName(selectedCandidate.memberName)}
                  </h2>

                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-1 flex-wrap">
                    {canViewCandidateSensitive(selectedCandidate) && selectedCandidate.memberIcNo && (
                      <span className="font-mono text-slate-500 font-medium">
                        No. IC: <strong className="text-slate-700">{selectedCandidate.memberIcNo}</strong>
                      </span>
                    )}
                    {selectedCandidate.invoiceNo && (
                      <>
                        <span className="text-slate-300">•</span>
                        <span className="font-mono text-slate-400 text-[11px]">
                          Inv: {selectedCandidate.invoiceNo}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Passing Rank Holding Notice */}
                  {selectedCandidate.gradingStatus === 'Lulus' && (
                    <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-md flex items-center gap-1 shadow-2xs">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span>Keputusan: Lulus Penilaian</span>
                      </span>
                      {cohortStatus && !cohortStatus.isAllTestsCompleted ? (
                        <span className="text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-md flex items-center gap-1 shadow-2xs">
                          <Clock className="w-3 h-3 text-amber-600 shrink-0" />
                          <span>Pangkat bengkung profil kekal ({selectedCandidate.currentBengkung}) sehingga kesemua {cohortStatus.totalCandidates} calon selesai diuji</span>
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-amber-900 bg-amber-100 border border-amber-300 px-2.5 py-0.5 rounded-md flex items-center gap-1 shadow-2xs">
                          <Sparkles className="w-3 h-3 text-amber-600 shrink-0" />
                          <span>Pangkat bengkung profil rasmi: {selectedCandidate.targetBengkung}</span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Target Bengkung Scheme Card (Clean typography, no bengkung image) */}
              <div className="bg-gradient-to-br from-red-50/80 via-white to-red-50/40 border-2 border-red-200/90 rounded-2xl p-4 sm:p-5 text-center sm:text-right shadow-xs w-full md:w-auto md:min-w-[260px] shrink-0 flex flex-col justify-center">
                <div className="text-[10px] uppercase font-black tracking-widest text-red-600 flex items-center justify-center sm:justify-end gap-1.5">
                  <Award className="w-3.5 h-3.5 text-red-600 shrink-0" />
                  <span>Ujian Kenaikan Bengkung</span>
                </div>
                <div className="text-base sm:text-lg font-black text-slate-900 mt-1 leading-snug">
                  {activeScheme.displayName}
                </div>
                <div className="text-xs text-slate-600 font-medium mt-1 flex items-center justify-center sm:justify-end gap-1.5">
                  <span>Syarat Lulus:</span>
                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-extrabold text-[11px]">
                    Minima {activeScheme.passingScorePercentage}%
                  </span>
                </div>
              </div>
            </div>

            {/* 4. PANEL PENILAI & MULTI-PENANDA MARKAH SECTION */}
            <div className="bg-white rounded-3xl border border-slate-200/90 p-4 sm:p-6 shadow-sm space-y-4">
              {/* Header: Title + Registered Count + Add Marker Action */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0 shadow-xs">
                    <Users className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-xs sm:text-sm font-black text-slate-900 uppercase tracking-wider">
                        Panel Penilai & Penanda Markah
                      </h3>
                      <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                        {candidateAssessments.length} / 3 Penilai Rasmi
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Pemarkahan silibus oleh panel penguji bertauliah (Bengkung Kuning ke atas). Maksimum 3 penilai bagi setiap calon.
                    </p>
                  </div>
                </div>

                {canUserMarkGrading && (
                  candidateAssessments.length >= 3 ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-slate-100 text-slate-400 border border-slate-200 self-start sm:self-auto">
                      <CheckCircle2 className="w-3.5 h-3.5 text-slate-400" />
                      <span>Maksimum 3 Penanda Dicapai</span>
                    </span>
                  ) : !isUserSuperAdmin && myExistingAssessment ? (
                    <button
                      type="button"
                      onClick={() => handleSwitchAssessment(myExistingAssessment.id)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition shadow-xs cursor-pointer self-start sm:self-auto ${activeAssessmentId === myExistingAssessment.id
                        ? 'bg-slate-900 text-white ring-2 ring-slate-900/10'
                        : 'bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200'
                        }`}
                      title="Anda telah menilai calon ini. Klik untuk mengemas kini markah."
                    >
                      <Edit3 className="w-3.5 h-3.5 text-amber-500" />
                      <span>Kemas Kini Penilaian Anda</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      {activeAssessmentId === 'new' && (
                        <button
                          type="button"
                          onClick={handleCancelNewMarker}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition shadow-xs cursor-pointer"
                          title="Batal penambahan penanda markah baharu"
                        >
                          <X className="w-3.5 h-3.5 text-slate-500" />
                          <span>Batal Penilai</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleSwitchAssessment('new')}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-extrabold transition shadow-xs cursor-pointer ${activeAssessmentId === 'new'
                          ? 'bg-red-600 text-white shadow-red-200 shadow-md ring-2 ring-red-400/30'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                          }`}
                      >
                        <Plus className="w-4 h-4" />
                        <span>Tambah Penanda Markah</span>
                      </button>
                    </div>
                  )
                )}
              </div>

              {/* Superadmin Quick Examiner Switcher Strip */}
              {canUserMarkGrading && isUserSuperAdmin && (
                <div className="bg-amber-50/80 border border-amber-200/90 rounded-2xl p-3 sm:px-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-2xs">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs font-extrabold text-slate-900 flex items-center gap-1.5">
                        <span>Tandai Sebagai Penguji</span>
                        <span className="text-[9px] font-black px-1.5 py-0.2 rounded bg-amber-200/80 text-amber-900">
                          Mod Superadmin
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500">
                        Pilih penanda daripada senarai pengurusan untuk mengisi atau mengemas kini bagi pihak
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1 md:max-w-md justify-end min-w-0">
                    <select
                      value={examinerName}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setExaminerName(newName);
                        const existing = candidateAssessments.find(
                          (a) => a.assessedBy.toLowerCase().trim() === newName.toLowerCase().trim()
                        );
                        if (existing) {
                          setActiveAssessmentId(existing.id);
                          loadAssessmentIntoForm(existing);
                          showToast('info', 'Borang Sedia Ada', `${newName} telah menilai calon ini. Anda kini boleh mengemas kini penilaian ini.`);
                        } else {
                          if (candidateAssessments.length >= 3) {
                            showToast('warning', 'Had Maksimum Penanda', 'Calon ini telah mempunyai 3 orang penanda markah. Sila pilih penilai sedia ada.');
                            return;
                          }
                          setActiveAssessmentId('new');
                          initFreshScores(selectedCandidate?.targetBengkung || 'Putih 1');
                          setExaminerName(newName);
                        }
                      }}
                      className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:border-red-600 cursor-pointer shadow-2xs truncate min-w-0"
                    >
                      {managementMembersList.map((m) => {
                        const alreadyMarked = candidateAssessments.some(
                          (a) => a.assessedBy.toLowerCase().trim() === m.name.toLowerCase().trim()
                        );
                        return (
                          <option key={m.id} value={m.name}>
                            {m.name} — {getHighestRole(m)} ({m.gelanggang || 'ASMM'}){alreadyMarked ? ' ✓ [Telah Menilai - Kemas Kini]' : ''}
                          </option>
                        );
                      })}
                    </select>
                    <span className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 shrink-0 whitespace-nowrap text-center sm:text-left">
                      {resolvedExaminerRole}
                    </span>
                  </div>
                </div>
              )}

              {/* Unified Responsive 3-Slot Assessor Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-1">
                {/* Existing Assessors Slots */}
                {candidateAssessments.map((ass, idx) => {
                  const isActive = activeAssessmentId === ass.id;
                  const isPassed = ass.overallStatus === 'Lulus';
                  const isMyAssessment = Boolean(
                    (currentUser?.name && ass.assessedBy.toLowerCase().trim() === currentUser.name.toLowerCase().trim()) ||
                    (currentUser?.id && (ass as any).assessorId === currentUser.id) ||
                    (currentUser?.id && (ass as any).assessedById === currentUser.id) ||
                    (currentUser?.id && managementMembersList.find(m => m.name.toLowerCase().trim() === ass.assessedBy.toLowerCase().trim())?.id === currentUser.id)
                  );
                  const canDelete = isUserSuperAdmin || isMyAssessment;

                  return (
                    <div
                      key={ass.id}
                      onClick={() => handleSwitchAssessment(ass.id)}
                      className={`rounded-2xl p-4 transition-all cursor-pointer relative flex flex-col justify-between border-2 text-left ${isActive
                        ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-900/10'
                        : 'bg-white hover:bg-slate-50/80 text-slate-800 border-slate-200/90 hover:border-slate-300 shadow-2xs'
                        }`}
                    >
                      {/* Top Badge & Delete */}
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${isActive ? 'bg-white/10 text-amber-300' : 'bg-slate-100 text-slate-600'
                            }`}
                        >
                          Penilai {idx + 1}
                        </span>

                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-[10px] font-black px-2 py-0.5 rounded-full ${isPassed
                              ? isActive
                                ? 'bg-emerald-500 text-white'
                                : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              : isActive
                                ? 'bg-red-500 text-white'
                                : 'bg-red-100 text-red-800 border border-red-200'
                              }`}
                          >
                            {ass.averageScore}% • {ass.overallStatus}
                          </span>

                          {canDelete && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget({ id: ass.id, name: ass.assessedBy });
                              }}
                              className={`p-1.5 rounded-lg transition cursor-pointer flex items-center gap-1 ${isActive
                                ? 'hover:bg-slate-800 text-slate-400 hover:text-red-300'
                                : 'hover:bg-rose-50 text-slate-400 hover:text-rose-600'
                                }`}
                              title={isUserSuperAdmin && !isMyAssessment ? `Batalkan Penilai ${ass.assessedBy} (Superadmin)` : "Batalkan Penilaian Anda"}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Assessor Name & Role */}
                      <div className="my-3 min-w-0">
                        <h4 className={`text-xs sm:text-sm font-black truncate leading-snug ${isActive ? 'text-white' : 'text-slate-900'}`}>
                          {ass.assessedBy}
                        </h4>
                        <p className={`text-[11px] truncate mt-0.5 ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
                          {ass.assessorRole || 'Penguji ASMM'}
                        </p>
                      </div>

                      {/* Active Status Footer */}
                      <div className="pt-2 border-t border-slate-100/10 flex items-center justify-between text-[10px]">
                        {isActive ? (
                          <span className="font-extrabold text-amber-300 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping" />
                            <span>Aktif</span>
                          </span>
                        ) : (
                          <span className="text-slate-400 font-semibold flex items-center gap-1">
                            <span>Papar</span>
                            <ChevronRight className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* New Form In Progress Slot (if activeAssessmentId === 'new') */}
                {activeAssessmentId === 'new' && (() => {
                  const isMyNewAssessor = Boolean(
                    (currentUser?.name && (examinerName || '').toLowerCase().trim() === currentUser.name.toLowerCase().trim()) ||
                    (currentUser?.id && managementMembersList.find(m => m.name.toLowerCase().trim() === (examinerName || '').toLowerCase().trim())?.id === currentUser.id)
                  );
                  const canCancelNew = isUserSuperAdmin || isMyNewAssessor;

                  return (
                    <div className="rounded-2xl p-4 border-2 border-dashed border-red-500 bg-red-50/70 text-left flex flex-col justify-between shadow-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-red-100 text-red-800">
                          Penilai {candidateAssessments.length + 1} (Baharu)
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-red-600 text-white flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            <span>Sedang Mengisi</span>
                          </span>
                          {canCancelNew && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelNewMarker();
                              }}
                              className="p-1 rounded-lg hover:bg-red-200 text-red-700 hover:text-red-900 transition cursor-pointer"
                              title={isUserSuperAdmin && !isMyNewAssessor ? "Batalkan Penilai Baharu (Superadmin)" : "Batalkan Penilai Baharu"}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="my-3 min-w-0">
                        <h4 className="text-xs sm:text-sm font-black text-red-950 truncate leading-snug">
                          {examinerName || currentUser?.name || 'Penguji ASMM'}
                        </h4>
                        <p className="text-[11px] text-red-700 truncate mt-0.5">
                          {resolvedExaminerRole}
                        </p>
                      </div>

                      <div className="pt-2 border-t border-red-200/80 flex items-center justify-between gap-2 text-[10px]">
                        <span className="font-bold text-red-600 truncate">
                          Menilai mengikut kriteria sukatan di bawah
                        </span>
                        {canCancelNew && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelNewMarker();
                            }}
                            className="px-2.5 py-1 rounded-lg bg-white hover:bg-red-100 text-red-700 font-extrabold text-[10px] border border-red-200 shadow-2xs transition cursor-pointer shrink-0"
                            title="Batalkan penambahan penilai ini daripada panel calon"
                          >
                            Batal Penilai
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Empty Slot (if slots remain and not currently filling 'new') */}
                {candidateAssessments.length < 3 && activeAssessmentId !== 'new' && canUserMarkGrading && (
                  <button
                    type="button"
                    onClick={() => handleSwitchAssessment('new')}
                    className="rounded-2xl p-4 border-2 border-dashed border-slate-200 hover:border-red-400 hover:bg-red-50/40 text-slate-500 hover:text-red-700 transition flex flex-col items-center justify-center gap-2 cursor-pointer group min-h-[120px]"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-red-100 text-slate-400 group-hover:text-red-600 flex items-center justify-center transition">
                      <Plus className="w-4 h-4" />
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-black">
                        + Tambah Penilai {candidateAssessments.length + 1}
                      </div>
                      <div className="text-[10px] text-slate-400 group-hover:text-red-500 mt-0.5">
                        Slot kosong (Maksimum 3 orang)
                      </div>
                    </div>
                  </button>
                )}
              </div>

              {/* Consensus Summary Banner if multiple examiners */}
              {panelSummary && panelSummary.count > 1 && (
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-2xl p-3.5 sm:px-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
                  <div className="flex items-center gap-2.5 text-xs">
                    <div className="w-8 h-8 rounded-xl bg-amber-400/20 text-amber-300 flex items-center justify-center shrink-0">
                      <Trophy className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-300 font-medium">
                        Purata Markah Panel ({panelSummary.count} Penilai Berdaftar):
                      </div>
                      <div className="text-sm sm:text-base font-black text-white">
                        {panelSummary.avgScore}% Purata Keseluruhan
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    <span className="text-[11px] text-slate-300 font-bold hidden sm:inline">Keputusan:</span>
                    <span
                      className={`text-xs font-extrabold px-3 py-1 rounded-full ${panelSummary.consensusStatus === 'Lulus'
                        ? 'bg-emerald-500 text-white shadow-xs'
                        : panelSummary.consensusStatus === 'Mengulang'
                          ? 'bg-red-500 text-white shadow-xs'
                          : 'bg-amber-500 text-white shadow-xs'
                        }`}
                    >
                      {panelSummary.consensusStatus.toUpperCase()} ({panelSummary.passCount}/{panelSummary.count} Penilai Lulus)
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions & Live Score Banner */}
            <div className={`bg-white rounded-2xl border border-slate-200 p-3.5 sm:p-4 shadow-xs flex flex-col md:flex-row items-stretch md:items-center gap-3 sm:gap-4 ${canUserMarkGrading ? 'justify-between' : 'justify-end'
              }`}>
              {/* Quick Batch Scoring (Examiners only) */}
              {canUserMarkGrading && (
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => handleMarkAllPassed(5)}
                    className="flex-1 sm:flex-none px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs font-bold rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Semua 5/5</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMarkAllPassed(4)}
                    className="flex-1 sm:flex-none px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 text-xs font-bold rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                    <span>Semua 4/5</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleResetScores}
                    className="px-2.5 py-1.5 text-slate-500 hover:text-slate-800 cursor-pointer bg-slate-50 border border-slate-200 text-xs font-semibold rounded-xl transition shrink-0"
                    title="Kosongkan semula markah"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Live Score Display */}
              <div className="flex items-center justify-around bg-slate-50 px-3 py-2 rounded-xl border border-slate-200 gap-2 sm:gap-4">
                <div className="text-center">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Markah</div>
                  <div className="text-xs sm:text-sm font-black text-slate-900">
                    {scoreSummary.totalScore} / {scoreSummary.maxScore}
                  </div>
                </div>
                <div className="h-5 w-px bg-slate-200" />
                <div className="text-center">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Peratus</div>
                  <div className={`text-xs sm:text-sm font-black ${!scoreSummary.isCompleted
                    ? 'text-slate-800'
                    : scoreSummary.isPassed
                      ? 'text-emerald-600'
                      : 'text-red-600'
                    }`}>
                    {scoreSummary.percentage}%
                  </div>
                </div>
                <div className="h-5 w-px bg-slate-200" />
                <div className="text-center">
                  <div className="text-[10px] text-slate-500 font-bold uppercase">Keputusan</div>
                  {!scoreSummary.isCompleted ? (
                    <div className="text-[11px] sm:text-xs font-black px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                      -
                    </div>
                  ) : scoreSummary.isPassed ? (
                    <div className="text-[11px] sm:text-xs font-extrabold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                      LULUS
                    </div>
                  ) : (
                    <div className="text-[11px] sm:text-xs font-extrabold px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                      MENGULANG
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Rubric Criteria Items List (Extracted directly from senarai ujian putih kuning.pdf) */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between text-xs font-bold text-slate-600 px-1 flex-wrap gap-2">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span>Sukatan Pelajaran ({activeScheme.criteria.length} Kriteria)</span>
                  {/* Auto-Save Status Pill */}
                  {canUserMarkGrading && (
                    <div className="flex items-center gap-1.5">
                      {autoSaveStatus === 'saving' && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 animate-pulse">
                          <RotateCcw className="w-3 h-3 animate-spin text-amber-600" />
                          <span>Autosimpan...</span>
                        </span>
                      )}
                      {autoSaveStatus === 'saved' && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          <span>
                            Autosimpan Disimpan{' '}
                            {lastAutoSavedAt
                              ? `• ${lastAutoSavedAt.toLocaleTimeString('ms-MY', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}`
                              : ''}
                          </span>
                        </span>
                      )}
                      {autoSaveStatus === 'idle' && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-50 text-slate-600 border border-slate-200">
                          <Cloud className="w-3 h-3 text-emerald-600" />
                          <span>Autosimpan Aktif</span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className="text-slate-500 font-medium">
                  Selesai: <strong>{scoreSummary.ratedCount}</strong> / {scoreSummary.criteriaCount}
                </span>
              </div>

              {activeScheme.criteria.map((item) => {
                const currentScore = currentScores[item.id]?.score || 0;
                const currentNote = currentScores[item.id]?.notes || '';

                return (
                  <div
                    key={item.id}
                    className={`bg-white rounded-2xl p-3.5 sm:p-5 border transition-all ${currentScore > 0 ? 'border-slate-200 shadow-2xs' : 'border-slate-200/70 bg-slate-50/30'
                      }`}
                  >
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 sm:gap-4">
                      {/* Left Item Description: Badges on top, Numbering aligned horizontally with Title */}
                      <div className="flex-1 min-w-0">
                        {/* Badges row: placed above title, aligned with title text start */}
                        <div className="flex items-center gap-1.5 flex-wrap ml-[38px] sm:ml-[44px] mb-1">
                          <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                            {item.category}
                          </span>
                          {item.isUlangkaji && (
                            <span className="text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                              Ulangkaji
                            </span>
                          )}
                        </div>

                        {/* Numbering + Title Row (Matching horizontal top coordinate) */}
                        <div className="flex items-start gap-2.5 sm:gap-3">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-slate-900 text-white font-black text-xs flex items-center justify-center shrink-0">
                            {item.orderNumber}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 leading-snug">
                              {item.title}
                            </h4>
                            {item.description && (
                              <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 leading-relaxed">
                                {item.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right Scoring Controls: 1 to 5 Buttons (Fully responsive & Permission-Aware) */}
                      <div className="flex items-center justify-between sm:justify-end gap-1 sm:gap-1.5 w-full md:w-auto shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                        {[1, 2, 3, 4, 5].map((scoreVal) => {
                          const isSelected = currentScore === scoreVal;
                          return (
                            <button
                              key={scoreVal}
                              type="button"
                              disabled={!canUserMarkGrading}
                              onClick={() => handleScoreChange(item.id, scoreVal)}
                              className={`flex-1 sm:flex-none w-10 sm:w-11 h-11 rounded-xl font-black text-xs transition-all flex flex-col items-center justify-center ${!canUserMarkGrading
                                ? isSelected
                                  ? scoreVal >= 5
                                    ? 'bg-emerald-600 text-white opacity-80 cursor-not-allowed'
                                    : scoreVal >= 3
                                      ? 'bg-blue-600 text-white opacity-80 cursor-not-allowed'
                                      : 'bg-red-600 text-white opacity-80 cursor-not-allowed'
                                  : 'bg-slate-100 text-slate-300 border border-slate-200/60 cursor-not-allowed'
                                : isSelected
                                  ? scoreVal >= 5
                                    ? 'bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-300 cursor-pointer active:scale-95'
                                    : scoreVal >= 3
                                      ? 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-300 cursor-pointer active:scale-95'
                                      : 'bg-red-600 text-white shadow-sm ring-2 ring-red-300 cursor-pointer active:scale-95'
                                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200/80 cursor-pointer active:scale-95'
                                }`}
                            >
                              <span className="text-sm font-black">{scoreVal}</span>
                              <span className="text-[8px] font-normal leading-none opacity-80">
                                {scoreVal >= 5 ? 'Kemas' : scoreVal >= 3 ? 'Lulus' : 'Lemah'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Optional Note / Examiner Feedback input */}
                    {canUserMarkGrading ? (
                      <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide shrink-0">
                          Catatan:
                        </span>
                        <input
                          type="text"
                          placeholder="Ulasan pergerakan (contoh: kekuda kemas, elakan mantap)..."
                          value={currentNote}
                          onChange={(e) => handleNotesChange(item.id, e.target.value)}
                          className="text-xs text-slate-700 w-full bg-transparent border-b border-slate-200 focus:border-red-600 focus:outline-none pb-0.5"
                        />
                      </div>
                    ) : currentNote ? (
                      <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center gap-2 text-xs">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide shrink-0">
                          Catatan:
                        </span>
                        <span className="text-slate-700 italic">"{currentNote}"</span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {/* Examiner Information & Final Signoff Block */}
            {canUserMarkGrading ? (
              /* ACTIVE EXAMINER / SUPERADMIN WORKSPACE */
              <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-6 shadow-xs space-y-4">
                {/* Clean Header Bar */}
                <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <h3 className="text-xs sm:text-sm font-extrabold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-red-600 shrink-0" />
                    <span>Pengesahan & Catatan Dewan Penguji ASMM</span>
                  </h3>
                </div>

                {/* Active Examiner Info Summary (Fully responsive on mobile) */}
                <div className="bg-slate-50/80 p-3 sm:p-3.5 rounded-2xl border border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0 shadow-2xs">
                      <User className="w-4 h-4 text-amber-300" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        Borang Penanda:
                      </div>
                      <div className="text-xs font-bold text-slate-900 truncate">
                        {examinerName}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap self-start sm:self-auto">
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-lg bg-red-50 text-red-700 border border-red-200 shrink-0">
                      {resolvedExaminerRole}
                    </span>
                    {(activeAssessmentId === 'new' || isCurrentAssessmentEditable) && (
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${activeAssessmentId === 'new'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : 'bg-amber-50 text-amber-800 border-amber-200'
                          }`}
                      >
                        {activeAssessmentId === 'new' ? 'Borang Baharu' : 'Mod Kemas Kini'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Overall Examiner Remarks (Catatan) */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Ulasan Keseluruhan Pentaksiran:
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Catatan prestasi silibus, amanah pemakaian bengkung, dan ulasan penambahbaikan pesilat..."
                    value={examinerRemarks}
                    onChange={(e) => setExaminerRemarks(e.target.value)}
                    disabled={!isCurrentAssessmentEditable}
                    className="w-full px-3.5 py-2.5 text-xs font-medium rounded-xl border border-slate-200 focus:outline-none focus:border-red-600 bg-slate-50/50 disabled:opacity-60 disabled:cursor-not-allowed leading-relaxed"
                  />
                </div>

                {/* Primary Action: Simpan & Sahkan Penilaian Rasmi */}
                <div className="pt-2">
                  <button
                    type="button"
                    disabled={isSaving || !isCurrentAssessmentEditable}
                    onClick={handleSaveAssessment}
                    title={!isCurrentAssessmentEditable ? 'Borang ini ditanda oleh penilai lain' : undefined}
                    className="w-full py-3.5 px-6 rounded-2xl bg-red-600 hover:bg-red-700 active:scale-98 text-white text-xs sm:text-sm font-black transition shadow-sm hover:shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                        <span>Menyimpan Keputusan...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-amber-300" />
                        <span>
                          {activeAssessmentId === 'new'
                            ? 'Simpan & Sahkan Penilaian Rasmi'
                            : 'Kemas Kini Penilaian'}
                        </span>
                      </>
                    )}
                  </button>
                </div>

                {/* Secondary Navigation Row: Kembali & Calon Pager */}
                <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                  <button
                    type="button"
                    onClick={handleBackToList}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold transition cursor-pointer shadow-2xs"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 text-slate-400" />
                    <span>Kembali ke Senarai Calon</span>
                  </button>

                  {/* Clean 2-Column Next/Prev Grid that NEVER breaks or wraps awkwardly */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => prevCandidate && handleSelectCandidate(prevCandidate)}
                      disabled={!prevCandidate}
                      className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs font-bold transition shadow-2xs select-none ${prevCandidate
                        ? 'bg-white hover:bg-slate-100 text-slate-800 border border-slate-200 cursor-pointer active:scale-98'
                        : 'bg-slate-100 text-slate-400 border border-slate-200/60 cursor-not-allowed opacity-50'
                        }`}
                      title={prevCandidate ? `Calon Sebelumnya: ${formatPublicName(prevCandidate.memberName)}` : 'Awal senarai'}
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Sebelumnya</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => nextCandidate && handleSelectCandidate(nextCandidate)}
                      disabled={!nextCandidate}
                      className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs font-black transition shadow-2xs select-none ${nextCandidate
                        ? 'bg-slate-900 hover:bg-slate-800 text-white cursor-pointer active:scale-98'
                        : 'bg-slate-100 text-slate-400 border border-slate-200/60 cursor-not-allowed opacity-50'
                        }`}
                      title={nextCandidate ? `Calon Seterusnya: ${formatPublicName(nextCandidate.memberName)}` : 'Akhir senarai'}
                    >
                      <span>Calon Seterusnya</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* CETAK SLIP: ONLY BOTTOM MOST AFTER CATATAN */}
                {selectedCandidate.assessment && canViewCandidateSensitive(selectedCandidate) && (
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-center sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setIsPrintSlipOpen(true)}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition shadow-xs cursor-pointer active:scale-98"
                    >
                      <Printer className="w-4 h-4 text-amber-400" />
                      <span>Cetak Slip Pentaksiran Calon</span>
                    </button>
                  </div>
                )}
              </div>
            ) : hasExaminerContent ? (
              /* AHLI BIASA VIEW: ONLY SHOW RESULT OF PENGESAHAN & CATATAN DEWAN PENGUJI ASMM */
              <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-6 shadow-xs space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <h3 className="text-xs sm:text-sm font-extrabold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <span>Pengesahan & Catatan Dewan Penguji ASMM</span>
                  </h3>
                  <div className="text-[11px] text-slate-500 font-medium flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 font-bold text-xs">
                      <User className="w-3.5 h-3.5 text-slate-500" />
                      <span>Penguji: {selectedCandidate.assessment?.assessedBy || 'Penguji Bertauliah ASMM'}</span>
                      {selectedCandidate.assessment?.assessorRole && (
                        <span className="text-[10px] text-slate-400 font-normal">
                          ({selectedCandidate.assessment.assessorRole})
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Overall Examiner Remarks Result */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Ulasan Keseluruhan Pentaksiran:
                  </label>
                  <div className="w-full px-3.5 py-3 text-xs font-medium rounded-xl border border-slate-200 bg-slate-50/70 text-slate-800 leading-relaxed italic">
                    {selectedCandidate.assessment?.examinerRemarks?.trim() ? (
                      `"${selectedCandidate.assessment.examinerRemarks}"`
                    ) : (
                      <span className="text-slate-400 not-italic">Tiada catatan ulasan bertulis daripada penguji.</span>
                    )}
                  </div>
                </div>

                {/* Return and Calon Navigation Actions */}
                <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                  <button
                    type="button"
                    onClick={handleBackToList}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold transition cursor-pointer shadow-2xs"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 text-slate-400" />
                    <span>Kembali ke Senarai Calon</span>
                  </button>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => prevCandidate && handleSelectCandidate(prevCandidate)}
                      disabled={!prevCandidate}
                      className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs font-bold transition shadow-2xs select-none ${prevCandidate
                        ? 'bg-white hover:bg-slate-100 text-slate-800 border border-slate-200 cursor-pointer active:scale-98'
                        : 'bg-slate-100 text-slate-400 border border-slate-200/60 cursor-not-allowed opacity-50'
                        }`}
                      title={prevCandidate ? `Calon Sebelumnya: ${formatPublicName(prevCandidate.memberName)}` : 'Awal senarai'}
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span>Sebelumnya</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => nextCandidate && handleSelectCandidate(nextCandidate)}
                      disabled={!nextCandidate}
                      className={`inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs font-black transition shadow-2xs select-none ${nextCandidate
                        ? 'bg-slate-900 hover:bg-slate-800 text-white cursor-pointer active:scale-98'
                        : 'bg-slate-100 text-slate-400 border border-slate-200/60 cursor-not-allowed opacity-50'
                        }`}
                      title={nextCandidate ? `Calon Seterusnya: ${formatPublicName(nextCandidate.memberName)}` : 'Akhir senarai'}
                    >
                      <span>Calon Seterusnya</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* CETAK SLIP: ONLY BOTTOM MOST AFTER CATATAN */}
                {selectedCandidate.assessment && canViewCandidateSensitive(selectedCandidate) && (
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-center sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setIsPrintSlipOpen(true)}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition shadow-xs cursor-pointer active:scale-98"
                    >
                      <Printer className="w-4 h-4 text-amber-400" />
                      <span>Cetak Slip Pentaksiran Calon</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* AHLI BIASA VIEW: NO CONTENT FROM PENGUJI -> NO PENGUJI SECTION RENDERED */
              <div className="pt-2 flex items-center justify-start">
                <button
                  type="button"
                  onClick={handleBackToList}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 transition cursor-pointer shadow-xs"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Kembali ke Senarai Calon</span>
                </button>
              </div>
            )}

            {/* 3. PRINTABLE OFFICIAL SLIP PREVIEW & PORTAL (WHEN REQUESTED) */}
            {isPrintSlipOpen && (
              <>
                {/* On-Screen Modal / Card Preview */}
                <div className="bg-white rounded-3xl border-2 border-slate-900 p-5 sm:p-8 shadow-lg space-y-4 print:hidden">
                  {/* Top Header Bar with Close Button */}
                  <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                    <div className="inline-flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse"></span>
                      <span className="text-xs font-black uppercase tracking-wider text-slate-900">
                        Pratonton Slip Rasmi Pentaksiran
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsPrintSlipOpen(false)}
                      className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-full transition cursor-pointer"
                      title="Tutup Paparan Slip"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Official Letterhead */}
                  <div className="border-b-2 border-slate-900 pb-3 text-center">
                    <div className="flex items-center justify-center gap-3 mb-1.5">
                      <img src="/logo-pssasmm.png" alt="PSSASMM" className="h-12 w-auto object-contain" />
                    </div>
                    <h2 className="font-black text-sm sm:text-base text-slate-900 uppercase tracking-tight leading-tight">
                      PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA NEGERI KEDAH DARUL AMAN
                    </h2>
                    <p className="text-[11px] font-mono font-bold text-red-600 mt-0.5">
                      Pendaftaran JPPM/ROS: PPM-007-02-28082022
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      No. 1, Jalan Warisan, Taman Warisan, 09600 Lunas, Kedah Darul Aman • Emel: rasmi@asmm.org.my
                    </p>
                  </div>

                  {/* Slip Title & Cert Ref */}
                  <div className="text-center space-y-1">
                    <div className="inline-block px-5 py-1 rounded bg-slate-900 text-white font-black text-xs uppercase tracking-wider">
                      SLIP KEPUTUSAN PENTAKSIRAN UJIAN KENAIKAN BENGKUNG
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      No. Sijil: <strong className="text-slate-700">{selectedCandidate.assessment?.certificateNo || `ASMM/GRD/${new Date().getFullYear()}/001`}</strong> • Tarikh Cetakan: {new Date().toLocaleDateString('ms-MY')}
                    </div>
                  </div>

                  {/* Tabulated Candidate & Exam Information */}
                  <div className="border border-slate-300 rounded-lg overflow-hidden">
                    <table className="w-full text-xs border-collapse">
                      <tbody>
                        <tr className="border-b border-slate-300">
                          <td className="w-1/4 py-2 px-3 bg-slate-100 font-bold text-slate-700 border-r border-slate-300">
                            Nama Pesilat
                          </td>
                          <td className="w-1/4 py-2 px-3 font-extrabold text-slate-900 border-r border-slate-300">
                            {formatPublicName(selectedCandidate.memberName)}
                          </td>
                          <td className="w-1/4 py-2 px-3 bg-slate-100 font-bold text-slate-700 border-r border-slate-300">
                            No. Ahli / MyKad
                          </td>
                          <td className="w-1/4 py-2 px-3 font-mono text-slate-900">
                            {selectedCandidate.memberId}
                            {canViewCandidateSensitive(selectedCandidate) && selectedCandidate.memberIcNo ? ` (${selectedCandidate.memberIcNo})` : ''}
                          </td>
                        </tr>
                        <tr className="border-b border-slate-300">
                          <td className="py-2 px-3 bg-slate-100 font-bold text-slate-700 border-r border-slate-300">
                            Gelanggang Latihan
                          </td>
                          <td className="py-2 px-3 text-slate-900 border-r border-slate-300">
                            {selectedCandidate.gelanggang}
                          </td>
                          <td className="py-2 px-3 bg-slate-100 font-bold text-slate-700 border-r border-slate-300">
                            Tarikh Ujian
                          </td>
                          <td className="py-2 px-3 text-slate-900">
                            {examDate}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 bg-slate-100 font-bold text-slate-700 border-r border-slate-300">
                            Kenaikan Bengkung
                          </td>
                          <td className="py-2 px-3 font-black text-slate-900 border-r border-slate-300">
                            {selectedCandidate.currentBengkung} → <span className="text-red-600">{activeScheme.displayName}</span>
                          </td>
                          <td className="py-2 px-3 bg-slate-100 font-bold text-slate-700 border-r border-slate-300">
                            Keputusan Rasmi
                          </td>
                          <td className="py-2 px-3 font-black text-emerald-700 text-sm">
                            {panelSummary
                              ? `${panelSummary.consensusStatus} (${panelSummary.avgScore}%)`
                              : `${scoreSummary.grade} (${scoreSummary.percentage}%)`}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Tabulated Criteria Breakdown Tables — Each Category in a Separate Table */}
                  <div className="space-y-4">
                    {groupedCriteria.map((group) => (
                      <div key={group.category} className="space-y-1.5">
                        <div className="flex items-center justify-between px-1">
                          <span className="text-[11px] font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-red-600 inline-block"></span>
                            <span>Kategori: {group.category}</span>
                          </span>
                          <span className="text-[10px] font-mono font-semibold text-slate-400">
                            {group.items.length} kriteria
                          </span>
                        </div>

                        <div className="border border-slate-300 rounded-lg overflow-x-auto">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead>
                              <tr className="bg-slate-100 text-slate-800 font-extrabold border-b border-slate-300 text-[11px]">
                                <th className="py-2 px-2 w-10 text-center border-r border-slate-300">No</th>
                                <th className="py-2 px-3 text-left border-r border-slate-300">Kriteria Silibus Rasmi</th>
                                <th className="py-2 px-1.5 w-14 text-center border-r border-slate-300">Juri 1</th>
                                <th className="py-2 px-1.5 w-14 text-center border-r border-slate-300">Juri 2</th>
                                <th className="py-2 px-1.5 w-14 text-center border-r border-slate-300">Juri 3</th>
                                <th className="py-2 px-1.5 w-16 text-center border-r border-slate-300 bg-slate-200/80 font-black">Purata</th>
                                <th className="py-2 px-3 w-32 text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 font-medium">
                              {group.items.map((item) => {
                                const s1 = slipJuri1?.scores?.[item.id]?.score;
                                const s2 = slipJuri2?.scores?.[item.id]?.score;
                                const s3 = slipJuri3?.scores?.[item.id]?.score;

                                const validScores: number[] = [];
                                if (typeof s1 === 'number') validScores.push(s1);
                                if (typeof s2 === 'number') validScores.push(s2);
                                if (typeof s3 === 'number') validScores.push(s3);

                                const avgVal = validScores.length > 0
                                  ? (validScores.reduce((sum, v) => sum + v, 0) / validScores.length).toFixed(1)
                                  : '-';
                                const numAvg = avgVal !== '-' ? parseFloat(avgVal) : (s1 ?? 0);
                                const rowStatus = numAvg >= 4 ? 'Cemerlang' : numAvg >= 3 ? 'Lulus' : 'Belum Menguasai';

                                return (
                                  <tr key={item.id} className="hover:bg-slate-50/50">
                                    <td className="py-1.5 px-2 text-center font-bold text-slate-500 border-r border-slate-300">{item.orderNumber}</td>
                                    <td className="py-1.5 px-3 font-semibold text-slate-900 border-r border-slate-300 leading-tight">{item.title}</td>
                                    <td className="py-1.5 px-1.5 text-center font-bold text-slate-900 border-r border-slate-300">
                                      {typeof s1 === 'number' ? `${s1}/5` : '—'}
                                    </td>
                                    <td className="py-1.5 px-1.5 text-center font-bold text-slate-900 border-r border-slate-300">
                                      {typeof s2 === 'number' ? `${s2}/5` : '—'}
                                    </td>
                                    <td className="py-1.5 px-1.5 text-center font-bold text-slate-900 border-r border-slate-300">
                                      {typeof s3 === 'number' ? `${s3}/5` : '—'}
                                    </td>
                                    <td className="py-1.5 px-1.5 text-center font-black text-slate-900 border-r border-slate-300 bg-slate-50">
                                      {avgVal !== '-' ? `${avgVal}/5` : '—'}
                                    </td>
                                    <td className="py-1.5 px-3 text-center">
                                      <span
                                        className={`text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap inline-block ${numAvg >= 4
                                          ? 'bg-emerald-100 text-emerald-800'
                                          : numAvg >= 3
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-red-100 text-red-800'
                                          }`}
                                      >
                                        {rowStatus}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tabulated Panel Juri & Catatan Section */}
                  <div className="space-y-1.5">
                    <div className="text-[11px] font-black uppercase text-slate-700 tracking-wider flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-red-600" />
                      <span>Panel Juri Penilai & Catatan Pentaksiran</span>
                    </div>

                    <div className="border border-slate-300 rounded-lg overflow-hidden">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-100 text-slate-900 text-[10px] font-black uppercase border-b border-slate-300">
                            <th className="w-1/3 py-2 px-3 text-left border-r border-slate-300">Juri 1 (Penilai Utama)</th>
                            <th className="w-1/3 py-2 px-3 text-left border-r border-slate-300">Juri 2 (Penilai Kedua)</th>
                            <th className="w-1/3 py-2 px-3 text-left">Juri 3 (Penilai Ketiga)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="align-top divide-x divide-slate-300">
                            {/* JURI 1 */}
                            <td className="p-3 bg-white space-y-1.5">
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-extrabold text-slate-900 text-xs truncate" title={slipJuri1?.assessedBy || '—'}>
                                  {slipJuri1?.assessedBy || '—'}
                                </span>
                                {slipJuri1 && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 shrink-0">
                                    {slipJuri1.averageScore}% • {slipJuri1.overallStatus}
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {slipJuri1?.assessorRole || 'Penguji Bertauliah ASMM'}
                              </div>
                              <div className="p-2 rounded bg-slate-50 border border-slate-200 text-[10.5px] text-slate-700 leading-snug">
                                <span className="text-[9px] font-extrabold uppercase text-slate-400 block tracking-wider mb-0.5">
                                  Catatan Ulasan:
                                </span>
                                <span className="italic">
                                  "{slipJuri1?.examinerRemarks?.trim() ? slipJuri1.examinerRemarks : 'Menguasai silibus dengan baik.'}"
                                </span>
                              </div>
                            </td>

                            {/* JURI 2 */}
                            <td className="p-3 bg-white space-y-1.5">
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-extrabold text-slate-900 text-xs truncate" title={slipJuri2 ? slipJuri2.assessedBy : '(Slot Juri 2)'}>
                                  {slipJuri2 ? slipJuri2.assessedBy : '(Slot Juri 2)'}
                                </span>
                                {slipJuri2 ? (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 shrink-0">
                                    {slipJuri2.averageScore}% • {slipJuri2.overallStatus}
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 shrink-0">
                                    Belum Dinilai
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {slipJuri2 ? (slipJuri2.assessorRole || 'Penguji Bertauliah ASMM') : 'Penguji Bertauliah'}
                              </div>
                              {slipJuri2 ? (
                                <div className="p-2 rounded bg-slate-50 border border-slate-200 text-[10.5px] text-slate-700 leading-snug">
                                  <span className="text-[9px] font-extrabold uppercase text-slate-400 block tracking-wider mb-0.5">
                                    Catatan Ulasan:
                                  </span>
                                  <span className="italic">
                                    "{slipJuri2.examinerRemarks?.trim() ? slipJuri2.examinerRemarks : 'Prestasi memuaskan dan memenuhi piawaian silibus.'}"
                                  </span>
                                </div>
                              ) : (
                                <div className="p-2 rounded bg-slate-50 border border-slate-200 text-[10px] text-slate-400 italic text-center">
                                  Slot juri kedua terbuka untuk penilai bertauliah.
                                </div>
                              )}
                            </td>

                            {/* JURI 3 */}
                            <td className="p-3 bg-white space-y-1.5">
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-extrabold text-slate-900 text-xs truncate" title={slipJuri3 ? slipJuri3.assessedBy : '(Slot Juri 3)'}>
                                  {slipJuri3 ? slipJuri3.assessedBy : '(Slot Juri 3)'}
                                </span>
                                {slipJuri3 ? (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 shrink-0">
                                    {slipJuri3.averageScore}% • {slipJuri3.overallStatus}
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 shrink-0">
                                    Belum Dinilai
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {slipJuri3 ? (slipJuri3.assessorRole || 'Penguji Bertauliah ASMM') : 'Penguji Bertauliah'}
                              </div>
                              {slipJuri3 ? (
                                <div className="p-2 rounded bg-slate-50 border border-slate-200 text-[10.5px] text-slate-700 leading-snug">
                                  <span className="text-[9px] font-extrabold uppercase text-slate-400 block tracking-wider mb-0.5">
                                    Catatan Ulasan:
                                  </span>
                                  <span className="italic">
                                    "{slipJuri3.examinerRemarks?.trim() ? slipJuri3.examinerRemarks : 'Disahkan berdisiplin dan menguasai sukatan bengkung.'}"
                                  </span>
                                </div>
                              ) : (
                                <div className="p-2 rounded bg-slate-50 border border-slate-200 text-[10px] text-slate-400 italic text-center">
                                  Slot juri ketiga terbuka untuk penilai bertauliah.
                                </div>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Official Digital Copy Notice in Footer */}
                  <div className="pt-3 border-t border-slate-300 text-center space-y-0.5">
                    <p className="text-[11px] text-slate-600 font-medium italic">
                      Ini adalah cetakan dokumen komputer rasmi. Tiada tandatangan fizikal diperlukan.
                    </p>
                    <p className="text-[9px] text-slate-400 font-mono uppercase tracking-wider">
                      Persatuan Seni Silat Awang Sulung Merah Muda Negeri Kedah Darul Aman (No. ROS: PPM-007-02-28082022)
                    </p>
                  </div>

                  {/* Print Trigger Button */}
                  <div className="text-center pt-1 print:hidden">
                    <button
                      type="button"
                      onClick={() => window.print()}
                      className="px-6 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition shadow-md inline-flex items-center gap-2 cursor-pointer"
                    >
                      <Printer className="w-4 h-4 text-amber-400" />
                      <span>Cetak Sekarang (Ctrl + P)</span>
                    </button>
                  </div>
                </div>

                {/* Print Portal Mounted to document.body for Physical / PDF Print Output */}
                {typeof document !== 'undefined' && createPortal(
                  <div className="print-document-portal hidden print:block font-sans text-slate-900 bg-white w-full space-y-2">
                    {/* Official Letterhead */}
                    <div className="border-b-2 border-slate-900 pb-2 text-center print-avoid-break">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <img src="/logo-pssasmm.png" alt="PSSASMM" className="h-10 w-auto object-contain" />
                      </div>
                      <h2 className="font-black text-xs sm:text-sm uppercase tracking-tight text-slate-900 leading-tight">
                        PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA NEGERI KEDAH DARUL AMAN
                      </h2>
                      <p className="text-[9.5px] font-mono font-bold text-red-600 mt-0.5">
                        Pendaftaran JPPM/ROS: PPM-007-02-28082022
                      </p>
                      <p className="text-[8.5px] text-slate-500 mt-0.5">
                        No. 1, Jalan Warisan, Taman Warisan, 09600 Lunas, Kedah Darul Aman • Emel: rasmi@asmm.org.my
                      </p>
                    </div>

                    {/* Slip Title & Cert Ref */}
                    <div className="text-center space-y-0.5 print-avoid-break">
                      <div className="inline-block px-3 py-0.5 rounded bg-slate-900 text-white font-black text-[10.5px] uppercase tracking-wider">
                        SLIP KEPUTUSAN PENTAKSIRAN UJIAN KENAIKAN BENGKUNG
                      </div>
                      <div className="text-[9px] text-slate-600 font-mono">
                        No. Sijil: <strong className="text-slate-900">{selectedCandidate.assessment?.certificateNo || `ASMM/GRD/${new Date().getFullYear()}/001`}</strong> • Tarikh Cetakan: {new Date().toLocaleDateString('ms-MY')}
                      </div>
                    </div>

                    {/* Tabulated Candidate & Exam Information */}
                    <div className="print-avoid-break">
                      <table className="w-full text-xs border-collapse border border-slate-300" style={{ tableLayout: 'fixed' }}>
                        <tbody>
                          <tr className="border-b border-slate-300">
                            <td className="w-[22%] py-1 px-2.5 bg-slate-100 font-bold text-slate-700 border-r border-slate-300 text-[10px]">
                              Nama Pesilat
                            </td>
                            <td className="w-[28%] py-1 px-2.5 font-extrabold text-slate-900 border-r border-slate-300 text-[10px] truncate">
                              {formatPublicName(selectedCandidate.memberName)}
                            </td>
                            <td className="w-[22%] py-1 px-2.5 bg-slate-100 font-bold text-slate-700 border-r border-slate-300 text-[10px]">
                              No. Ahli / MyKad
                            </td>
                            <td className="w-[28%] py-1 px-2.5 font-mono text-slate-900 text-[10px] truncate">
                              {selectedCandidate.memberId}
                              {canViewCandidateSensitive(selectedCandidate) && selectedCandidate.memberIcNo ? ` (${selectedCandidate.memberIcNo})` : ''}
                            </td>
                          </tr>
                          <tr className="border-b border-slate-300">
                            <td className="w-[22%] py-1 px-2.5 bg-slate-100 font-bold text-slate-700 border-r border-slate-300 text-[10px]">
                              Gelanggang Latihan
                            </td>
                            <td className="w-[28%] py-1 px-2.5 text-slate-900 border-r border-slate-300 text-[10px] truncate">
                              {selectedCandidate.gelanggang}
                            </td>
                            <td className="w-[22%] py-1 px-2.5 bg-slate-100 font-bold text-slate-700 border-r border-slate-300 text-[10px]">
                              Tarikh Ujian
                            </td>
                            <td className="w-[28%] py-1 px-2.5 text-slate-900 text-[10px]">
                              {examDate}
                            </td>
                          </tr>
                          <tr>
                            <td className="w-[22%] py-1 px-2.5 bg-slate-100 font-bold text-slate-700 border-r border-slate-300 text-[10px]">
                              Kenaikan Bengkung
                            </td>
                            <td className="w-[28%] py-1 px-2.5 font-black text-slate-900 border-r border-slate-300 text-[10px]">
                              {selectedCandidate.currentBengkung} → <span className="text-red-600">{activeScheme.displayName}</span>
                            </td>
                            <td className="w-[22%] py-1 px-2.5 bg-slate-100 font-bold text-slate-700 border-r border-slate-300 text-[10px]">
                              Keputusan Rasmi
                            </td>
                            <td className="w-[28%] py-1 px-2.5 font-black text-emerald-700 text-[11px]">
                              {panelSummary
                                ? `${panelSummary.consensusStatus} (${panelSummary.avgScore}%)`
                                : `${scoreSummary.grade} (${scoreSummary.percentage}%)`}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Tabulated Criteria Breakdown Tables — Each Category in a Separate Table */}
                    <div className="space-y-2">
                      {groupedCriteria.map((group) => (
                        <div key={group.category} className="space-y-0.5 print-avoid-break">
                          <div className="flex items-center justify-between px-1.5 py-0.5 bg-slate-100 border border-slate-300 border-b-0 rounded-t">
                            <span className="text-[9px] font-black uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-600 inline-block"></span>
                              <span>Kategori: {group.category}</span>
                            </span>
                            <span className="text-[8px] font-mono font-semibold text-slate-500 lowercase">
                              ({group.items.length} kriteria)
                            </span>
                          </div>

                          <table className="w-full text-left text-xs border-collapse border border-slate-300" style={{ tableLayout: 'fixed' }}>
                            <thead>
                              <tr className="bg-slate-50 text-slate-800 font-extrabold border-b border-slate-300 text-[9.5px]">
                                <th className="py-0.5 px-1 w-[5%] text-center border-r border-slate-300">No</th>
                                <th className="py-0.5 px-2 w-[48%] text-left border-r border-slate-300">Kriteria Silibus Rasmi</th>
                                <th className="py-0.5 px-1 w-[7.5%] text-center border-r border-slate-300">Juri 1</th>
                                <th className="py-0.5 px-1 w-[7.5%] text-center border-r border-slate-300">Juri 2</th>
                                <th className="py-0.5 px-1 w-[7.5%] text-center border-r border-slate-300">Juri 3</th>
                                <th className="py-0.5 px-1 w-[8.5%] text-center border-r border-slate-300 bg-slate-200/80 font-black">Purata</th>
                                <th className="py-0.5 px-1.5 w-[16%] text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 font-medium text-[9.5px]">
                              {group.items.map((item) => {
                                const s1 = slipJuri1?.scores?.[item.id]?.score;
                                const s2 = slipJuri2?.scores?.[item.id]?.score;
                                const s3 = slipJuri3?.scores?.[item.id]?.score;

                                const validScores: number[] = [];
                                if (typeof s1 === 'number') validScores.push(s1);
                                if (typeof s2 === 'number') validScores.push(s2);
                                if (typeof s3 === 'number') validScores.push(s3);

                                const avgVal = validScores.length > 0
                                  ? (validScores.reduce((sum, v) => sum + v, 0) / validScores.length).toFixed(1)
                                  : '-';
                                const numAvg = avgVal !== '-' ? parseFloat(avgVal) : (s1 ?? 0);
                                const rowStatus = numAvg >= 4 ? 'Cemerlang' : numAvg >= 3 ? 'Lulus' : 'Belum Menguasai';

                                return (
                                  <tr key={item.id} className="border-b border-slate-200 print-avoid-break">
                                    <td className="py-0.5 px-1 text-center font-bold text-slate-500 border-r border-slate-300">{item.orderNumber}</td>
                                    <td className="py-0.5 px-2 font-semibold text-slate-900 border-r border-slate-300 leading-tight break-words">{item.title}</td>
                                    <td className="py-0.5 px-1 text-center font-bold text-slate-900 border-r border-slate-300">
                                      {typeof s1 === 'number' ? `${s1}/5` : '—'}
                                    </td>
                                    <td className="py-0.5 px-1 text-center font-bold text-slate-900 border-r border-slate-300">
                                      {typeof s2 === 'number' ? `${s2}/5` : '—'}
                                    </td>
                                    <td className="py-0.5 px-1 text-center font-bold text-slate-900 border-r border-slate-300">
                                      {typeof s3 === 'number' ? `${s3}/5` : '—'}
                                    </td>
                                    <td className="py-0.5 px-1 text-center font-black text-slate-900 border-r border-slate-300 bg-slate-50">
                                      {avgVal !== '-' ? `${avgVal}/5` : '—'}
                                    </td>
                                    <td className="py-0.5 px-1.5 text-center">
                                      <span
                                        className={`text-[8px] font-bold px-1.5 py-0.2 rounded whitespace-nowrap inline-block ${numAvg >= 4
                                          ? 'bg-emerald-100 text-emerald-800'
                                          : numAvg >= 3
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-red-100 text-red-800'
                                          }`}
                                      >
                                        {rowStatus}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>

                    {/* Tabulated Panel Juri & Catatan Section */}
                    <div className="space-y-1 print-avoid-break">
                      <div className="text-[10px] font-black uppercase text-slate-700 tracking-wider flex items-center gap-1">
                        <Users className="w-3 h-3 text-red-600" />
                        <span>Panel Juri Penilai & Catatan Pentaksiran</span>
                      </div>

                      <table className="w-full text-xs border-collapse border border-slate-300" style={{ tableLayout: 'fixed' }}>
                        <thead>
                          <tr className="bg-slate-100 text-slate-900 text-[9px] font-black uppercase border-b border-slate-300">
                            <th className="w-[33.33%] py-1 px-2 text-left border-r border-slate-300">Juri 1 (Penilai Utama)</th>
                            <th className="w-[33.33%] py-1 px-2 text-left border-r border-slate-300">Juri 2 (Penilai Kedua)</th>
                            <th className="w-[33.34%] py-1 px-2 text-left">Juri 3 (Penilai Ketiga)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="align-top divide-x divide-slate-300 text-[9.5px]">
                            {/* JURI 1 */}
                            <td className="p-1.5 bg-white space-y-1">
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-extrabold text-slate-900 truncate" title={slipJuri1?.assessedBy || '—'}>
                                  {slipJuri1?.assessedBy || '—'}
                                </span>
                                {slipJuri1 && (
                                  <span className="text-[8.5px] font-bold px-1 py-0.2 rounded bg-emerald-100 text-emerald-800 shrink-0">
                                    {slipJuri1.averageScore}% • {slipJuri1.overallStatus}
                                  </span>
                                )}
                              </div>
                              <div className="text-[8.5px] text-slate-500">
                                {slipJuri1?.assessorRole || 'Penguji Bertauliah ASMM'}
                              </div>
                              <div className="p-1 rounded bg-slate-50 border border-slate-200 text-[9px] text-slate-700 leading-snug">
                                <span className="text-[8px] font-extrabold uppercase text-slate-400 block tracking-wider mb-0.5">
                                  Catatan Ulasan:
                                </span>
                                <span className="italic">
                                  "{slipJuri1?.examinerRemarks?.trim() ? slipJuri1.examinerRemarks : 'Menguasai silibus dengan baik.'}"
                                </span>
                              </div>
                            </td>

                            {/* JURI 2 */}
                            <td className="p-1.5 bg-white space-y-1">
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-extrabold text-slate-900 truncate" title={slipJuri2 ? slipJuri2.assessedBy : '(Slot Juri 2)'}>
                                  {slipJuri2 ? slipJuri2.assessedBy : '(Slot Juri 2)'}
                                </span>
                                {slipJuri2 ? (
                                  <span className="text-[8.5px] font-bold px-1 py-0.2 rounded bg-emerald-100 text-emerald-800 shrink-0">
                                    {slipJuri2.averageScore}% • {slipJuri2.overallStatus}
                                  </span>
                                ) : (
                                  <span className="text-[8.5px] font-medium px-1 py-0.2 rounded bg-slate-100 text-slate-400 shrink-0">
                                    Belum Dinilai
                                  </span>
                                )}
                              </div>
                              <div className="text-[8.5px] text-slate-500">
                                {slipJuri2 ? (slipJuri2.assessorRole || 'Penguji Bertauliah ASMM') : 'Penguji Bertauliah'}
                              </div>
                              {slipJuri2 ? (
                                <div className="p-1 rounded bg-slate-50 border border-slate-200 text-[9px] text-slate-700 leading-snug">
                                  <span className="text-[8px] font-extrabold uppercase text-slate-400 block tracking-wider mb-0.5">
                                    Catatan Ulasan:
                                  </span>
                                  <span className="italic">
                                    "{slipJuri2.examinerRemarks?.trim() ? slipJuri2.examinerRemarks : 'Prestasi memuaskan dan memenuhi piawaian silibus.'}"
                                  </span>
                                </div>
                              ) : (
                                <div className="p-1 rounded bg-slate-50 border border-slate-200 text-[8.5px] text-slate-400 italic text-center">
                                  Slot juri kedua terbuka untuk penilai bertauliah.
                                </div>
                              )}
                            </td>

                            {/* JURI 3 */}
                            <td className="p-1.5 bg-white space-y-1">
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-extrabold text-slate-900 truncate" title={slipJuri3 ? slipJuri3.assessedBy : '(Slot Juri 3)'}>
                                  {slipJuri3 ? slipJuri3.assessedBy : '(Slot Juri 3)'}
                                </span>
                                {slipJuri3 ? (
                                  <span className="text-[8.5px] font-bold px-1 py-0.2 rounded bg-emerald-100 text-emerald-800 shrink-0">
                                    {slipJuri3.averageScore}% • {slipJuri3.overallStatus}
                                  </span>
                                ) : (
                                  <span className="text-[8.5px] font-medium px-1 py-0.2 rounded bg-slate-100 text-slate-400 shrink-0">
                                    Belum Dinilai
                                  </span>
                                )}
                              </div>
                              <div className="text-[8.5px] text-slate-500">
                                {slipJuri3 ? (slipJuri3.assessorRole || 'Penguji Bertauliah ASMM') : 'Penguji Bertauliah'}
                              </div>
                              {slipJuri3 ? (
                                <div className="p-1 rounded bg-slate-50 border border-slate-200 text-[9px] text-slate-700 leading-snug">
                                  <span className="text-[8px] font-extrabold uppercase text-slate-400 block tracking-wider mb-0.5">
                                    Catatan Ulasan:
                                  </span>
                                  <span className="italic">
                                    "{slipJuri3.examinerRemarks?.trim() ? slipJuri3.examinerRemarks : 'Disahkan berdisiplin dan menguasai sukatan bengkung.'}"
                                  </span>
                                </div>
                              ) : (
                                <div className="p-1 rounded bg-slate-50 border border-slate-200 text-[8.5px] text-slate-400 italic text-center">
                                  Slot juri ketiga terbuka untuk penilai bertauliah.
                                </div>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Official Digital Copy Notice in Footer */}
                    <div className="pt-2 border-t border-slate-300 text-center space-y-0.5 print-avoid-break">
                      <p className="text-[9px] text-slate-600 font-medium italic">
                        Ini adalah cetakan dokumen komputer rasmi. Tiada tandatangan fizikal diperlukan.
                      </p>
                      <p className="text-[8px] text-slate-400 font-mono uppercase tracking-wider">
                        Persatuan Seni Silat Awang Sulung Merah Muda Negeri Kedah Darul Aman (No. ROS: PPM-007-02-28082022)
                      </p>
                    </div>
                  </div>,
                  document.body
                )}
              </>
            )}
          </div>
        )}

        {/* Custom Confirmation Modal for Deleting Assessor Form */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-200 text-center animate-scale-in">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-base font-extrabold text-slate-900">
                Batalkan Penilai Daripada Panel?
              </h3>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                Adakah anda pasti ingin membatalkan penandaan markah oleh <strong>"{deleteTarget.name}"</strong>? Rekod penilaian penguji ini akan dikeluarkan daripada panel calon.
              </p>
              <div className="flex items-center gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 py-2.5 px-3 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 transition cursor-pointer"
                >
                  Tutup
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const target = deleteTarget;
                    setDeleteTarget(null);
                    await executeDeleteAssessment(target.id, target.name);
                  }}
                  className="flex-1 py-2.5 px-3 rounded-xl bg-red-600 text-white text-xs font-bold hover:bg-red-700 shadow-xs transition cursor-pointer"
                >
                  Ya, Batalkan Penilai
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
